// client/src/coms/views/canvas_parts/canvas_room_view_parts/airbrushUtils.js
import { clipContextToSelection } from './selectionUtils';
import { getSharedCanvasToolWebglSurface } from './layerUtils';

const AIRBRUSH_NOZZLES = {
  standard: {
    id: 'standard',
    label: '0.3mm airbrush',
    shortLabel: '0.3',
    nozzleMm: 0.3,
    flow: 1,
    description: 'Fixed 0.3mm gravity-feed style airbrush nozzle.',
  },
};

export const AIRBRUSH_NOZZLE_OPTIONS = [AIRBRUSH_NOZZLES.standard];
export const DEFAULT_AIRBRUSH_NOZZLE = 'standard';
export const DEFAULT_AIRBRUSH_DISTANCE = 100;
export const MIN_AIRBRUSH_DISTANCE = 5;
export const MAX_AIRBRUSH_DISTANCE = 200;
export const DEFAULT_AIRBRUSH_PAINT_FLOW = 20;
export const DEFAULT_AIRBRUSH_AIR_PRESSURE = 5;
export const DEFAULT_AIRBRUSH_NEEDLE_LIMIT = 100;

const AIRBRUSH_BASELINE_DISTANCE = 100;
const MIN_AIRBRUSH_SPRAY_DIAMETER = 2;
const MAX_AIRBRUSH_SPRAY_DIAMETER = 8192;
const AIRBRUSH_TIMER_INTERVAL_SECONDS = 0.1;
const AIRBRUSH_RENDERER_MAX_FRAME_SECONDS = 0.04;
const BRUSH_ROTATION_DIRECTION_SECONDS = 0.02;
const DOT_STREAM_SPACING_PX = 1.15;
const AIRBRUSH_REFERENCE_RESOLUTION = 2048;
const AIRBRUSH_MAX_RESOLUTION_SCALE = 128;
const AIRBRUSH_MIN_RENDERER_SIZE = 720;
const AIRBRUSH_MAX_RENDERER_SIZE = 8192;
const AIRBRUSH_ALLOWED_PAINT_FLOWS = [20, 40, 60, 80, 100];
const AIRBRUSH_ALLOWED_PRESSURES = [5, 10, 15, 20, 25, 30, 35];
const AIRBRUSH_MODE_COUNT = AIRBRUSH_ALLOWED_PRESSURES.length * AIRBRUSH_ALLOWED_PAINT_FLOWS.length;
const AIRBRUSH_STATE_LIMIT = 64;
const AIRBRUSH_STATE_TTL_MS = 1800;

const AIRBRUSH_GPU_MAX_DOTS_PER_PASS = 22000;
const AIRBRUSH_GPU_FLOATS_PER_VERTEX = 7;

// GPU airbrush pixels are premultiplied. Cache the inverse-alpha conversion
// once so the hot transfer loop does not divide every colour channel for
// every pixel on every animation frame.
const AIRBRUSH_UNPREMULTIPLY_SCALE = new Float32Array(256);
for (let alpha = 1; alpha < 256; alpha += 1) {
  AIRBRUSH_UNPREMULTIPLY_SCALE[alpha] = 255 / alpha;
}


const AIRBRUSH_RENDERER_SETTING_KEYS = [
  'brushRotationEnabled',
  'brushRotationPointRadius',
  'brushRotationPointAngle',
  'brushRotationSpeed',
  'dotEdgeAvoid',
  'dotFadeStart',
  'dotFadeStrength',
  'smallDiameter',
  'smallAmount',
  'smallSpeed',
  'smallOpacity',
  'smallCentreBias',
  'smallSoftness',
  'smallMin',
  'smallMax',
  'mediumDiameter',
  'mediumAmount',
  'mediumSpeed',
  'mediumOpacity',
  'mediumCentreBias',
  'mediumSoftness',
  'mediumMin',
  'mediumMax',
  'largeDiameter',
  'largeAmount',
  'largeSpeed',
  'largeOpacity',
  'largeCentreBias',
  'largeSoftness',
  'largeMin',
  'largeMax',
  'splatterEnabled',
  'splatterMinLines',
  'splatterMaxLines',
  'splatterMinLength',
  'splatterMaxLength',
  'splatterMinThickness',
  'splatterMaxThickness',
];

const DEFAULT_AIRBRUSH_RENDERER_SETTINGS = {
  brushRotationEnabled: 0,
  brushRotationPointRadius: 18,
  brushRotationPointAngle: 52,
  brushRotationSpeed: 1,

  dotEdgeAvoid: 0,
  dotFadeStart: 0.02,
  dotFadeStrength: 2.5,

  smallDiameter: 147,
  smallAmount: 2500,
  smallSpeed: 5,
  smallOpacity: 0.74,
  smallCentreBias: 3,
  smallSoftness: 0.27,
  smallMin: 1,
  smallMax: 1.5,

  mediumDiameter: 109,
  mediumAmount: 0,
  mediumSpeed: 1,
  mediumOpacity: 1,
  mediumCentreBias: 0,
  mediumSoftness: 0,
  mediumMin: 1,
  mediumMax: 2,

  largeDiameter: 10,
  largeAmount: 0,
  largeSpeed: 1,
  largeOpacity: 1,
  largeCentreBias: 0,
  largeSoftness: 0,
  largeMin: 6,
  largeMax: 8,

  splatterEnabled: 1,
  splatterMinLines: 1,
  splatterMaxLines: 5,
  splatterMinLength: 21,
  splatterMaxLength: 32,
  splatterMinThickness: 1,
  splatterMaxThickness: 1.7,
};

const makeMode = (mode) => mode;

const AIRBRUSH_SAVED_MODE_SETTINGS = {
  '5:20': makeMode({
    spreadDiameter: 193,
    brushRotation: { enabled: false, pointRadius: 18, pointAngle: 52, speed: 1 },
    dots: { spreadDiameter: 193, edgeAvoid: 0.04, edgeFadeStart: 0.11, edgeFadeStrength: 2 },
    small: { spreadDiameter: 193, amount: 1000, speed: 1, opacity: 1, centreBias: 26, softness: 0, minSize: 1, maxSize: 1.65 },
    medium: { spreadDiameter: 109, amount: 19, speed: 1, opacity: 1, centreBias: 0, softness: 0, minSize: 2, maxSize: 7 },
    large: { spreadDiameter: 109, amount: 1, speed: 1, opacity: 1, centreBias: 0, softness: 0, minSize: 4, maxSize: 14, splatter: { enabled: true, minLines: 1, maxLines: 5, minLength: 30, maxLength: 46, minThickness: 1, maxThickness: 1.7 } },
  }),
  '5:40': makeMode({
    spreadDiameter: 186.25,
    brushRotation: { enabled: false, pointRadius: 18, pointAngle: 52, speed: 1 },
    dots: { spreadDiameter: 186.25, edgeAvoid: 1.53, edgeFadeStart: 0.19, edgeFadeStrength: 2.35 },
    small: { spreadDiameter: 186.25, amount: 3250, speed: 1, opacity: 1, centreBias: 26, softness: 0, minSize: 1, maxSize: 1.65 },
    medium: { spreadDiameter: 109, amount: 34.25, speed: 1, opacity: 1, centreBias: 0, softness: 0, minSize: 2, maxSize: 7 },
    large: { spreadDiameter: 109, amount: 2, speed: 1, opacity: 1, centreBias: 0, softness: 0, minSize: 4, maxSize: 14, splatter: { enabled: true, minLines: 1, maxLines: 5, minLength: 30, maxLength: 46, minThickness: 1, maxThickness: 1.7 } },
  }),
  '5:60': makeMode({
    spreadDiameter: 179.5,
    brushRotation: { enabled: false, pointRadius: 18, pointAngle: 52, speed: 1 },
    dots: { spreadDiameter: 179.5, edgeAvoid: 3.02, edgeFadeStart: 0.27, edgeFadeStrength: 2.7 },
    small: { spreadDiameter: 179.5, amount: 5500, speed: 1, opacity: 1, centreBias: 26, softness: 0, minSize: 1, maxSize: 1.65 },
    medium: { spreadDiameter: 109, amount: 49.5, speed: 1, opacity: 1, centreBias: 0, softness: 0, minSize: 2, maxSize: 7 },
    large: { spreadDiameter: 109, amount: 3, speed: 1, opacity: 1, centreBias: 0, softness: 0, minSize: 4, maxSize: 14, splatter: { enabled: true, minLines: 1, maxLines: 5, minLength: 30, maxLength: 46, minThickness: 1, maxThickness: 1.7 } },
  }),
  '5:80': makeMode({
    spreadDiameter: 172.75,
    brushRotation: { enabled: false, pointRadius: 18, pointAngle: 52, speed: 1 },
    dots: { spreadDiameter: 172.75, edgeAvoid: 4.51, edgeFadeStart: 0.35, edgeFadeStrength: 3.05 },
    small: { spreadDiameter: 172.75, amount: 7750, speed: 1, opacity: 1, centreBias: 26, softness: 0, minSize: 1, maxSize: 1.65 },
    medium: { spreadDiameter: 109, amount: 64.75, speed: 1, opacity: 1, centreBias: 0, softness: 0, minSize: 2, maxSize: 7 },
    large: { spreadDiameter: 109, amount: 4, speed: 1, opacity: 1, centreBias: 0, softness: 0, minSize: 4, maxSize: 14, splatter: { enabled: true, minLines: 1, maxLines: 5, minLength: 30, maxLength: 46, minThickness: 1, maxThickness: 1.7 } },
  }),
  '5:100': makeMode({
    spreadDiameter: 166,
    brushRotation: { enabled: false, pointRadius: 18, pointAngle: 52, speed: 1 },
    dots: { spreadDiameter: 166, edgeAvoid: 6, edgeFadeStart: 0.43, edgeFadeStrength: 3.4 },
    small: { spreadDiameter: 166, amount: 10000, speed: 1, opacity: 1, centreBias: 26, softness: 0, minSize: 1, maxSize: 1.65 },
    medium: { spreadDiameter: 109, amount: 80, speed: 1, opacity: 1, centreBias: 0, softness: 0, minSize: 2, maxSize: 7 },
    large: { spreadDiameter: 109, amount: 5, speed: 1, opacity: 1, centreBias: 0, softness: 0, minSize: 4, maxSize: 14, splatter: { enabled: true, minLines: 1, maxLines: 5, minLength: 30, maxLength: 46, minThickness: 1, maxThickness: 1.7 } },
  }),

  '10:20': makeMode({
    spreadDiameter: 244,
    brushRotation: { enabled: false, pointRadius: 18, pointAngle: 52, speed: 1 },
    dots: { spreadDiameter: 244, edgeAvoid: 0.04, edgeFadeStart: 0.11, edgeFadeStrength: 3.2 },
    small: { spreadDiameter: 244, amount: 1500, speed: 2, opacity: 1, centreBias: 12, softness: 0, minSize: 1, maxSize: 1.65 },
    medium: { spreadDiameter: 109, amount: 8, speed: 1, opacity: 1, centreBias: 0, softness: 0, minSize: 1, maxSize: 4.1 },
    large: { spreadDiameter: 109, amount: 1, speed: 1, opacity: 1, centreBias: 0, softness: 0, minSize: 6, maxSize: 8, splatter: { enabled: true, minLines: 1, maxLines: 5, minLength: 21, maxLength: 32, minThickness: 1, maxThickness: 1.7 } },
  }),
  '10:40': makeMode({
    spreadDiameter: 226.25,
    brushRotation: { enabled: false, pointRadius: 18, pointAngle: 52, speed: 1 },
    dots: { spreadDiameter: 226.25, edgeAvoid: 0.04, edgeFadeStart: 0.11, edgeFadeStrength: 3.2 },
    small: { spreadDiameter: 226.25, amount: 8625, speed: 2, opacity: 1, centreBias: 12, softness: 0, minSize: 1, maxSize: 1.65 },
    medium: { spreadDiameter: 109, amount: 16.25, speed: 1, opacity: 1, centreBias: 0, softness: 0, minSize: 1, maxSize: 4.1 },
    large: { spreadDiameter: 109, amount: 2, speed: 1, opacity: 1, centreBias: 0, softness: 0, minSize: 5.5, maxSize: 7.5, splatter: { enabled: true, minLines: 1, maxLines: 5, minLength: 21, maxLength: 32, minThickness: 1, maxThickness: 1.7 } },
  }),
  '10:60': makeMode({
    spreadDiameter: 208.5,
    brushRotation: { enabled: false, pointRadius: 18, pointAngle: 52, speed: 1 },
    dots: { spreadDiameter: 208.5, edgeAvoid: 0.04, edgeFadeStart: 0.11, edgeFadeStrength: 3.2 },
    small: { spreadDiameter: 208.5, amount: 15750, speed: 2, opacity: 1, centreBias: 12, softness: 0, minSize: 1, maxSize: 1.65 },
    medium: { spreadDiameter: 109, amount: 24.5, speed: 1, opacity: 1, centreBias: 0, softness: 0, minSize: 1, maxSize: 4.1 },
    large: { spreadDiameter: 109, amount: 3, speed: 1, opacity: 1, centreBias: 0, softness: 0, minSize: 5, maxSize: 7, splatter: { enabled: true, minLines: 1, maxLines: 5, minLength: 21, maxLength: 32, minThickness: 1, maxThickness: 1.7 } },
  }),
  '10:80': makeMode({
    spreadDiameter: 190.75,
    brushRotation: { enabled: false, pointRadius: 18, pointAngle: 52, speed: 1 },
    dots: { spreadDiameter: 190.75, edgeAvoid: 0.04, edgeFadeStart: 0.11, edgeFadeStrength: 3.2 },
    small: { spreadDiameter: 190.75, amount: 22875, speed: 2, opacity: 1, centreBias: 12, softness: 0, minSize: 1, maxSize: 1.65 },
    medium: { spreadDiameter: 109, amount: 32.75, speed: 1, opacity: 1, centreBias: 0, softness: 0, minSize: 1, maxSize: 4.1 },
    large: { spreadDiameter: 109, amount: 4, speed: 1, opacity: 1, centreBias: 0, softness: 0, minSize: 4.5, maxSize: 6.5, splatter: { enabled: true, minLines: 1, maxLines: 5, minLength: 21, maxLength: 32, minThickness: 1, maxThickness: 1.7 } },
  }),
  '10:100': makeMode({
    spreadDiameter: 173,
    brushRotation: { enabled: false, pointRadius: 18, pointAngle: 52, speed: 1 },
    dots: { spreadDiameter: 173, edgeAvoid: 0.04, edgeFadeStart: 0.11, edgeFadeStrength: 3.2 },
    small: { spreadDiameter: 173, amount: 30000, speed: 2, opacity: 1, centreBias: 12, softness: 0, minSize: 1, maxSize: 1.65 },
    medium: { spreadDiameter: 109, amount: 41, speed: 1, opacity: 1, centreBias: 0, softness: 0, minSize: 1, maxSize: 4.1 },
    large: { spreadDiameter: 109, amount: 5, speed: 1, opacity: 1, centreBias: 0, softness: 0, minSize: 4, maxSize: 6, splatter: { enabled: true, minLines: 1, maxLines: 5, minLength: 21, maxLength: 32, minThickness: 1, maxThickness: 1.7 } },
  }),

  '15:20': makeMode({
    spreadDiameter: 147,
    brushRotation: { enabled: false, pointRadius: 18, pointAngle: 52, speed: 1 },
    dots: { spreadDiameter: 147, edgeAvoid: 0, edgeFadeStart: 0.02, edgeFadeStrength: 2.5 },
    small: { spreadDiameter: 147, amount: 2500, speed: 4, opacity: 0.74, centreBias: 5, softness: 0.27, minSize: 1, maxSize: 1.5 },
    medium: { spreadDiameter: 109, amount: 0, speed: 1, opacity: 1, centreBias: 0, softness: 0, minSize: 1, maxSize: 1.5 },
    large: { spreadDiameter: 10, amount: 0, speed: 1, opacity: 1, centreBias: 0, softness: 0, minSize: 6, maxSize: 8, splatter: { enabled: true, minLines: 1, maxLines: 5, minLength: 21, maxLength: 32, minThickness: 1, maxThickness: 1.7 } },
  }),
  '15:40': makeMode({
    spreadDiameter: 151.65,
    brushRotation: { enabled: false, pointRadius: 18, pointAngle: 52, speed: 1 },
    dots: { spreadDiameter: 151.65, edgeAvoid: 0.008, edgeFadeStart: 0.038, edgeFadeStrength: 2.6 },
    small: { spreadDiameter: 151.65, amount: 8625, speed: 3.9, opacity: 0.8015, centreBias: 6.2, softness: 0.2225, minSize: 1, maxSize: 1.53 },
    medium: { spreadDiameter: 109, amount: 0, speed: 1, opacity: 1, centreBias: 0, softness: 0, minSize: 1, maxSize: 2.42 },
    large: { spreadDiameter: 29.8, amount: 0, speed: 1, opacity: 1, centreBias: 0, softness: 0, minSize: 5.6, maxSize: 7.6, splatter: { enabled: true, minLines: 1, maxLines: 5, minLength: 21, maxLength: 32, minThickness: 1, maxThickness: 1.7 } },
  }),
  '15:60': makeMode({
    spreadDiameter: 156.3,
    brushRotation: { enabled: false, pointRadius: 18, pointAngle: 52, speed: 1 },
    dots: { spreadDiameter: 156.3, edgeAvoid: 0.016, edgeFadeStart: 0.056, edgeFadeStrength: 2.7 },
    small: { spreadDiameter: 156.3, amount: 14750, speed: 3.8, opacity: 0.863, centreBias: 7.4, softness: 0.175, minSize: 1, maxSize: 1.56 },
    medium: { spreadDiameter: 109, amount: 0, speed: 1, opacity: 1, centreBias: 0, softness: 0, minSize: 1, maxSize: 2.84 },
    large: { spreadDiameter: 49.6, amount: 0, speed: 1, opacity: 1, centreBias: 0, softness: 0, minSize: 5.2, maxSize: 7.2, splatter: { enabled: true, minLines: 1, maxLines: 5, minLength: 21, maxLength: 32, minThickness: 1, maxThickness: 1.7 } },
  }),
  '15:80': makeMode({
    spreadDiameter: 160.95,
    brushRotation: { enabled: false, pointRadius: 18, pointAngle: 52, speed: 1 },
    dots: { spreadDiameter: 160.95, edgeAvoid: 0.024, edgeFadeStart: 0.074, edgeFadeStrength: 2.8 },
    small: { spreadDiameter: 160.95, amount: 20875, speed: 3.7, opacity: 0.9245, centreBias: 8.6, softness: 0.1275, minSize: 1, maxSize: 1.59 },
    medium: { spreadDiameter: 109, amount: 0, speed: 1, opacity: 1, centreBias: 0, softness: 0, minSize: 1, maxSize: 3.26 },
    large: { spreadDiameter: 69.4, amount: 0, speed: 1, opacity: 1, centreBias: 0, softness: 0, minSize: 4.8, maxSize: 6.8, splatter: { enabled: true, minLines: 1, maxLines: 5, minLength: 21, maxLength: 32, minThickness: 1, maxThickness: 1.7 } },
  }),
  '15:100': makeMode({
    spreadDiameter: 165.6,
    brushRotation: { enabled: false, pointRadius: 18, pointAngle: 52, speed: 1 },
    dots: { spreadDiameter: 165.6, edgeAvoid: 0.032, edgeFadeStart: 0.092, edgeFadeStrength: 2.9 },
    small: { spreadDiameter: 165.6, amount: 27000, speed: 3.6, opacity: 0.986, centreBias: 9.8, softness: 0.08, minSize: 1, maxSize: 1.62 },
    medium: { spreadDiameter: 109, amount: 0, speed: 1, opacity: 1, centreBias: 0, softness: 0, minSize: 1, maxSize: 3.68 },
    large: { spreadDiameter: 89.2, amount: 0, speed: 1, opacity: 1, centreBias: 0, softness: 0, minSize: 4.4, maxSize: 6.4, splatter: { enabled: true, minLines: 1, maxLines: 5, minLength: 21, maxLength: 32, minThickness: 1, maxThickness: 1.7 } },
  }),

  '20:20': makeMode({
    spreadDiameter: 147,
    brushRotation: { enabled: false, pointRadius: 18, pointAngle: 52, speed: 1 },
    dots: { spreadDiameter: 147, edgeAvoid: 0, edgeFadeStart: 0.02, edgeFadeStrength: 2.5 },
    small: { spreadDiameter: 147, amount: 2500, speed: 5, opacity: 0.74, centreBias: 3, softness: 0.27, minSize: 1, maxSize: 1.5 },
    medium: { spreadDiameter: 109, amount: 0, speed: 1, opacity: 1, centreBias: 0, softness: 0, minSize: 1, maxSize: 1.5 },
    large: { spreadDiameter: 10, amount: 0, speed: 1, opacity: 1, centreBias: 0, softness: 0, minSize: 6, maxSize: 8, splatter: { enabled: true, minLines: 1, maxLines: 5, minLength: 21, maxLength: 32, minThickness: 1, maxThickness: 1.7 } },
  }),
  '20:40': makeMode({
    spreadDiameter: 149.8,
    brushRotation: { enabled: false, pointRadius: 18, pointAngle: 52, speed: 1 },
    dots: { spreadDiameter: 149.8, edgeAvoid: 0.006, edgeFadeStart: 0.0335, edgeFadeStrength: 2.525 },
    small: { spreadDiameter: 149.8, amount: 7875, speed: 5.05, opacity: 0.798, centreBias: 4.15, softness: 0.2425, minSize: 1, maxSize: 1.5225 },
    medium: { spreadDiameter: 109, amount: 0, speed: 1, opacity: 1, centreBias: 0, softness: 0, minSize: 1, maxSize: 2.315 },
    large: { spreadDiameter: 24.85, amount: 0, speed: 1, opacity: 1, centreBias: 0, softness: 0, minSize: 5.7, maxSize: 7.7, splatter: { enabled: true, minLines: 1, maxLines: 5, minLength: 21, maxLength: 32, minThickness: 1, maxThickness: 1.7 } },
  }),
  '20:60': makeMode({
    spreadDiameter: 152.6,
    brushRotation: { enabled: false, pointRadius: 18, pointAngle: 52, speed: 1 },
    dots: { spreadDiameter: 152.6, edgeAvoid: 0.012, edgeFadeStart: 0.047, edgeFadeStrength: 2.55 },
    small: { spreadDiameter: 152.6, amount: 13250, speed: 5.1, opacity: 0.856, centreBias: 5.3, softness: 0.215, minSize: 1, maxSize: 1.545 },
    medium: { spreadDiameter: 109, amount: 0, speed: 1, opacity: 1, centreBias: 0, softness: 0, minSize: 1, maxSize: 2.63 },
    large: { spreadDiameter: 39.7, amount: 0, speed: 1, opacity: 1, centreBias: 0, softness: 0, minSize: 5.4, maxSize: 7.4, splatter: { enabled: true, minLines: 1, maxLines: 5, minLength: 21, maxLength: 32, minThickness: 1, maxThickness: 1.7 } },
  }),
  '20:80': makeMode({
    spreadDiameter: 155.4,
    brushRotation: { enabled: false, pointRadius: 18, pointAngle: 52, speed: 1 },
    dots: { spreadDiameter: 155.4, edgeAvoid: 0.018, edgeFadeStart: 0.0605, edgeFadeStrength: 2.575 },
    small: { spreadDiameter: 155.4, amount: 18625, speed: 5.15, opacity: 0.914, centreBias: 6.45, softness: 0.1875, minSize: 1, maxSize: 1.5675 },
    medium: { spreadDiameter: 109, amount: 0, speed: 1, opacity: 1, centreBias: 0, softness: 0, minSize: 1, maxSize: 2.945 },
    large: { spreadDiameter: 54.55, amount: 0, speed: 1, opacity: 1, centreBias: 0, softness: 0, minSize: 5.1, maxSize: 7.1, splatter: { enabled: true, minLines: 1, maxLines: 5, minLength: 21, maxLength: 32, minThickness: 1, maxThickness: 1.7 } },
  }),
  '20:100': makeMode({
    spreadDiameter: 158.2,
    brushRotation: { enabled: false, pointRadius: 18, pointAngle: 52, speed: 1 },
    dots: { spreadDiameter: 158.2, edgeAvoid: 0.024, edgeFadeStart: 0.074, edgeFadeStrength: 2.6 },
    small: { spreadDiameter: 158.2, amount: 24000, speed: 5.2, opacity: 0.972, centreBias: 7.6, softness: 0.16, minSize: 1, maxSize: 1.59 },
    medium: { spreadDiameter: 109, amount: 0, speed: 1, opacity: 1, centreBias: 0, softness: 0, minSize: 1, maxSize: 3.26 },
    large: { spreadDiameter: 69.4, amount: 0, speed: 1, opacity: 1, centreBias: 0, softness: 0, minSize: 4.8, maxSize: 6.8, splatter: { enabled: true, minLines: 1, maxLines: 5, minLength: 21, maxLength: 32, minThickness: 1, maxThickness: 1.7 } },
  }),

  '25:20': makeMode({
    spreadDiameter: 136,
    brushRotation: { enabled: false, pointRadius: 18, pointAngle: 52, speed: 1 },
    dots: { spreadDiameter: 136, edgeAvoid: 0, edgeFadeStart: 0.02, edgeFadeStrength: 1.7 },
    small: { spreadDiameter: 136, amount: 2500, speed: 6, opacity: 0.74, centreBias: 1, softness: 0.27, minSize: 1, maxSize: 1.5 },
    medium: { spreadDiameter: 109, amount: 0, speed: 1, opacity: 1, centreBias: 0, softness: 0, minSize: 1, maxSize: 1.5 },
    large: { spreadDiameter: 10, amount: 0, speed: 1, opacity: 1, centreBias: 0, softness: 0, minSize: 6, maxSize: 8, splatter: { enabled: true, minLines: 1, maxLines: 5, minLength: 21, maxLength: 32, minThickness: 1, maxThickness: 1.7 } },
  }),
  '25:40': makeMode({
    spreadDiameter: 139.7,
    brushRotation: { enabled: false, pointRadius: 18, pointAngle: 52, speed: 1 },
    dots: { spreadDiameter: 139.7, edgeAvoid: 0.004, edgeFadeStart: 0.029, edgeFadeStrength: 1.85 },
    small: { spreadDiameter: 139.7, amount: 7125, speed: 6.2, opacity: 0.7945, centreBias: 2.1, softness: 0.2625, minSize: 1, maxSize: 1.515 },
    medium: { spreadDiameter: 109, amount: 0, speed: 1, opacity: 1, centreBias: 0, softness: 0, minSize: 1, maxSize: 2.21 },
    large: { spreadDiameter: 19.9, amount: 0, speed: 1, opacity: 1, centreBias: 0, softness: 0, minSize: 5.8, maxSize: 7.8, splatter: { enabled: true, minLines: 1, maxLines: 5, minLength: 21, maxLength: 32, minThickness: 1, maxThickness: 1.7 } },
  }),
  '25:60': makeMode({
    spreadDiameter: 143.4,
    brushRotation: { enabled: false, pointRadius: 18, pointAngle: 52, speed: 1 },
    dots: { spreadDiameter: 143.4, edgeAvoid: 0.008, edgeFadeStart: 0.038, edgeFadeStrength: 2 },
    small: { spreadDiameter: 143.4, amount: 11750, speed: 6.4, opacity: 0.849, centreBias: 3.2, softness: 0.255, minSize: 1, maxSize: 1.53 },
    medium: { spreadDiameter: 109, amount: 0, speed: 1, opacity: 1, centreBias: 0, softness: 0, minSize: 1, maxSize: 2.42 },
    large: { spreadDiameter: 29.8, amount: 0, speed: 1, opacity: 1, centreBias: 0, softness: 0, minSize: 5.6, maxSize: 7.6, splatter: { enabled: true, minLines: 1, maxLines: 5, minLength: 21, maxLength: 32, minThickness: 1, maxThickness: 1.7 } },
  }),
  '25:80': makeMode({
    spreadDiameter: 147.1,
    brushRotation: { enabled: false, pointRadius: 18, pointAngle: 52, speed: 1 },
    dots: { spreadDiameter: 147.1, edgeAvoid: 0.012, edgeFadeStart: 0.047, edgeFadeStrength: 2.15 },
    small: { spreadDiameter: 147.1, amount: 16375, speed: 6.6, opacity: 0.9035, centreBias: 4.3, softness: 0.2475, minSize: 1, maxSize: 1.545 },
    medium: { spreadDiameter: 109, amount: 0, speed: 1, opacity: 1, centreBias: 0, softness: 0, minSize: 1, maxSize: 2.63 },
    large: { spreadDiameter: 39.7, amount: 0, speed: 1, opacity: 1, centreBias: 0, softness: 0, minSize: 5.4, maxSize: 7.4, splatter: { enabled: true, minLines: 1, maxLines: 5, minLength: 21, maxLength: 32, minThickness: 1, maxThickness: 1.7 } },
  }),
  '25:100': makeMode({
    spreadDiameter: 150.8,
    brushRotation: { enabled: false, pointRadius: 18, pointAngle: 52, speed: 1 },
    dots: { spreadDiameter: 150.8, edgeAvoid: 0.016, edgeFadeStart: 0.056, edgeFadeStrength: 2.3 },
    small: { spreadDiameter: 150.8, amount: 21000, speed: 6.8, opacity: 0.958, centreBias: 5.4, softness: 0.24, minSize: 1, maxSize: 1.56 },
    medium: { spreadDiameter: 109, amount: 0, speed: 1, opacity: 1, centreBias: 0, softness: 0, minSize: 1, maxSize: 2.84 },
    large: { spreadDiameter: 49.6, amount: 0, speed: 1, opacity: 1, centreBias: 0, softness: 0, minSize: 5.2, maxSize: 7.2, splatter: { enabled: true, minLines: 1, maxLines: 5, minLength: 21, maxLength: 32, minThickness: 1, maxThickness: 1.7 } },
  }),

  '30:20': makeMode({
    spreadDiameter: 136,
    brushRotation: { enabled: false, pointRadius: 18, pointAngle: 52, speed: 1 },
    dots: { spreadDiameter: 136, edgeAvoid: 0, edgeFadeStart: 0.02, edgeFadeStrength: 1.7 },
    small: { spreadDiameter: 136, amount: 2500, speed: 8, opacity: 0.74, centreBias: 1, softness: 0.27, minSize: 1, maxSize: 1.5 },
  }),
  '30:40': makeMode({
    spreadDiameter: 137.85,
    brushRotation: { enabled: false, pointRadius: 18, pointAngle: 52, speed: 1 },
    dots: { spreadDiameter: 137.85, edgeAvoid: 0.002, edgeFadeStart: 0.0245, edgeFadeStrength: 1.775 },
    small: { spreadDiameter: 137.85, amount: 6375, speed: 8.1, opacity: 0.791, centreBias: 1.55, softness: 0.2825, minSize: 1, maxSize: 1.5075 },
  }),
  '30:60': makeMode({
    spreadDiameter: 139.7,
    brushRotation: { enabled: false, pointRadius: 18, pointAngle: 52, speed: 1 },
    dots: { spreadDiameter: 139.7, edgeAvoid: 0.004, edgeFadeStart: 0.029, edgeFadeStrength: 1.85 },
    small: { spreadDiameter: 139.7, amount: 10250, speed: 8.2, opacity: 0.842, centreBias: 2.1, softness: 0.295, minSize: 1, maxSize: 1.515 },
  }),
  '30:80': makeMode({
    spreadDiameter: 141.55,
    brushRotation: { enabled: false, pointRadius: 18, pointAngle: 52, speed: 1 },
    dots: { spreadDiameter: 141.55, edgeAvoid: 0.006, edgeFadeStart: 0.0335, edgeFadeStrength: 1.925 },
    small: { spreadDiameter: 141.55, amount: 14125, speed: 8.3, opacity: 0.893, centreBias: 2.65, softness: 0.3075, minSize: 1, maxSize: 1.5225 },
  }),
  '30:100': makeMode({
    spreadDiameter: 143.4,
    brushRotation: { enabled: false, pointRadius: 18, pointAngle: 52, speed: 1 },
    dots: { spreadDiameter: 143.4, edgeAvoid: 0.008, edgeFadeStart: 0.038, edgeFadeStrength: 2 },
    small: { spreadDiameter: 143.4, amount: 18000, speed: 8.4, opacity: 0.944, centreBias: 3.2, softness: 0.32, minSize: 1, maxSize: 1.53 },
  }),

  '35:20': makeMode({
    spreadDiameter: 136,
    brushRotation: { enabled: false, pointRadius: 18, pointAngle: 52, speed: 1 },
    dots: { spreadDiameter: 136, edgeAvoid: 0, edgeFadeStart: 0.02, edgeFadeStrength: 1.7 },
    small: { spreadDiameter: 136, amount: 5000, speed: 10, opacity: 0.74, centreBias: 1, softness: 0.27, minSize: 1, maxSize: 1.5 },
    medium: { spreadDiameter: 109, amount: 200, speed: 1, opacity: 1, centreBias: 0, softness: 0, minSize: 1, maxSize: 1.5 },
    large: { spreadDiameter: 10, amount: 0, speed: 1, opacity: 1, centreBias: 0, softness: 0, minSize: 6, maxSize: 8, splatter: { enabled: true, minLines: 1, maxLines: 5, minLength: 21, maxLength: 32, minThickness: 1, maxThickness: 1.7 } },
  }),
  '35:40': makeMode({
    spreadDiameter: 136,
    brushRotation: { enabled: false, pointRadius: 18, pointAngle: 52, speed: 1 },
    dots: { spreadDiameter: 136, edgeAvoid: 0, edgeFadeStart: 0.02, edgeFadeStrength: 1.7 },
    small: { spreadDiameter: 136, amount: 11000, speed: 10, opacity: 0.7875, centreBias: 1, softness: 0.325, minSize: 1, maxSize: 1.5 },
    medium: { spreadDiameter: 109, amount: 900, speed: 1, opacity: 1, centreBias: 0, softness: 0, minSize: 1, maxSize: 1.5 },
    large: { spreadDiameter: 10, amount: 0, speed: 1, opacity: 1, centreBias: 0, softness: 0, minSize: 6, maxSize: 8, splatter: { enabled: true, minLines: 1, maxLines: 5, minLength: 21, maxLength: 32, minThickness: 1, maxThickness: 1.7 } },
  }),
  '35:60': makeMode({
    spreadDiameter: 136,
    brushRotation: { enabled: false, pointRadius: 18, pointAngle: 52, speed: 1 },
    dots: { spreadDiameter: 136, edgeAvoid: 0, edgeFadeStart: 0.02, edgeFadeStrength: 1.7 },
    small: { spreadDiameter: 136, amount: 17000, speed: 10, opacity: 0.835, centreBias: 1, softness: 0.38, minSize: 1, maxSize: 1.5 },
    medium: { spreadDiameter: 109, amount: 1600, speed: 1, opacity: 1, centreBias: 0, softness: 0, minSize: 1, maxSize: 1.5 },
    large: { spreadDiameter: 10, amount: 0, speed: 1, opacity: 1, centreBias: 0, softness: 0, minSize: 6, maxSize: 8, splatter: { enabled: true, minLines: 1, maxLines: 5, minLength: 21, maxLength: 32, minThickness: 1, maxThickness: 1.7 } },
  }),
  '35:80': makeMode({
    spreadDiameter: 136,
    brushRotation: { enabled: false, pointRadius: 18, pointAngle: 52, speed: 1 },
    dots: { spreadDiameter: 136, edgeAvoid: 0, edgeFadeStart: 0.02, edgeFadeStrength: 1.7 },
    small: { spreadDiameter: 136, amount: 22000, speed: 10, opacity: 0.8825, centreBias: 1, softness: 0.435, minSize: 1, maxSize: 1.5 },
    medium: { spreadDiameter: 109, amount: 2300, speed: 1, opacity: 1, centreBias: 0, softness: 0, minSize: 1, maxSize: 1.5 },
    large: { spreadDiameter: 10, amount: 0, speed: 1, opacity: 1, centreBias: 0, softness: 0, minSize: 6, maxSize: 8, splatter: { enabled: true, minLines: 1, maxLines: 5, minLength: 21, maxLength: 32, minThickness: 1, maxThickness: 1.7 } },
  }),
  '35:100': makeMode({
    spreadDiameter: 136,
    brushRotation: { enabled: false, pointRadius: 18, pointAngle: 52, speed: 1 },
    dots: { spreadDiameter: 136, edgeAvoid: 0, edgeFadeStart: 0.02, edgeFadeStrength: 1.7 },
    small: { spreadDiameter: 136, amount: 30000, speed: 10, opacity: 0.93, centreBias: 1, softness: 0.49, minSize: 1, maxSize: 1.5 },
    medium: { spreadDiameter: 109, amount: 3000, speed: 1, opacity: 1, centreBias: 0, softness: 0, minSize: 1, maxSize: 1.5 },
    large: { spreadDiameter: 10, amount: 0, speed: 1, opacity: 1, centreBias: 0, softness: 0, minSize: 6, maxSize: 8, splatter: { enabled: true, minLines: 1, maxLines: 5, minLength: 21, maxLength: 32, minThickness: 1, maxThickness: 1.7 } },
  }),
};

const airbrushRenderState = new Map();

const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
const rand = (a, b) => a + Math.random() * (b - a);
const randInt = (a, b) => Math.floor(rand(Math.ceil(a), Math.floor(b) + 1));
const num = (v, f = 0) => (Number.isFinite(Number(v)) ? Number(v) : f);
const has = (o, k) => Object.prototype.hasOwnProperty.call(o || {}, k);

const smoothstep = (a, b, v) => {
  const t = clamp((v - a) / Math.max(0.0001, b - a), 0, 1);
  return t * t * (3 - 2 * t);
};

const isRect = (r) => (
  r
  && Number.isFinite(r.x)
  && Number.isFinite(r.y)
  && Number.isFinite(r.width)
  && Number.isFinite(r.height)
);

const merge = (a, b) => {
  if (!isRect(a)) return isRect(b) ? b : null;
  if (!isRect(b)) return a;

  const x = Math.min(a.x, b.x);
  const y = Math.min(a.y, b.y);
  const right = Math.max(a.x + a.width, b.x + b.width);
  const bottom = Math.max(a.y + a.height, b.y + b.height);

  return { x, y, width: right - x, height: bottom - y };
};

function readWholeNumber(value, fallback) {
  const number = Number(value);
  return Number.isFinite(number) ? Math.round(number) : fallback;
}

function readNumber(value, fallback) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function isAllowedPaintFlow(value) {
  return AIRBRUSH_ALLOWED_PAINT_FLOWS.includes(readWholeNumber(value, -1));
}

function isAllowedPressure(value) {
  return AIRBRUSH_ALLOWED_PRESSURES.includes(readWholeNumber(value, -1));
}

function getAirbrushPressurePsi(options = {}) {
  return readWholeNumber(options.airbrushAirPressure, DEFAULT_AIRBRUSH_AIR_PRESSURE);
}

function getAirbrushPaintFlowPercent(options = {}) {
  return readWholeNumber(options.airbrushPaintFlow ?? options.airbrushFlow, DEFAULT_AIRBRUSH_PAINT_FLOW);
}

function getAirbrushModeKey(options = {}) {
  return `${getAirbrushPressurePsi(options)}:${getAirbrushPaintFlowPercent(options)}`;
}

function rgb(hex) {
  const raw = String(hex || '#000').replace('#', '');
  const full = raw.length === 3 ? raw.split('').map((x) => x + x).join('') : raw;
  const value = parseInt(full, 16);

  return Number.isFinite(value)
    ? {
      r: (value >> 16) & 255,
      g: (value >> 8) & 255,
      b: value & 255,
    }
    : {
      r: 0,
      g: 0,
      b: 0,
    };
}

function getAirbrushRgb(options = {}) {
  if (options.rgba) {
    const match = String(options.rgba).match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i);

    if (match) {
      return {
        r: clamp(Number(match[1]) || 0, 0, 255),
        g: clamp(Number(match[2]) || 0, 0, 255),
        b: clamp(Number(match[3]) || 0, 0, 255),
      };
    }
  }

  return rgb(options.strokeColor || options.color || '#000000');
}

function createCanvas(width, height = width) {
  if (typeof OffscreenCanvas !== 'undefined') {
    return new OffscreenCanvas(width, height);
  }

  if (typeof document !== 'undefined') {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    return canvas;
  }

  return null;
}

function clearCanvas(canvas) {
  if (!canvas) return;

  const ctx = canvas.getContext('2d');

  if (!ctx) return;

  ctx.clearRect(0, 0, canvas.width, canvas.height);
}

function oldOpacity(value, fallbackValue) {
  const n = num(value, fallbackValue);
  return n > 1 ? clamp(n / 100, 0, 1) : clamp(n, 0, 1);
}

function flattenRendererSettings(source) {
  if (!source || typeof source !== 'object') return null;

  const flat = { ...DEFAULT_AIRBRUSH_RENDERER_SETTINGS };

  if (source.brushRotation && typeof source.brushRotation === 'object') {
    if (has(source.brushRotation, 'enabled')) flat.brushRotationEnabled = source.brushRotation.enabled ? 1 : 0;
    if (has(source.brushRotation, 'pointRadius')) flat.brushRotationPointRadius = source.brushRotation.pointRadius;
    if (has(source.brushRotation, 'pointAngle')) flat.brushRotationPointAngle = source.brushRotation.pointAngle;
    if (has(source.brushRotation, 'speed')) flat.brushRotationSpeed = source.brushRotation.speed;
  }

  if (source.dots && typeof source.dots === 'object') {
    if (has(source.dots, 'edgeAvoid')) flat.dotEdgeAvoid = source.dots.edgeAvoid;
    if (has(source.dots, 'edgeFadeStart')) flat.dotFadeStart = source.dots.edgeFadeStart;
    if (has(source.dots, 'edgeFadeStrength')) flat.dotFadeStrength = source.dots.edgeFadeStrength;
  }

  if (source.small && typeof source.small === 'object') {
    flat.smallDiameter = num(source.small.spreadDiameter, num(source.dots?.spreadDiameter, num(source.spreadDiameter, flat.smallDiameter)));
    flat.smallAmount = num(source.small.amount, flat.smallAmount);
    flat.smallSpeed = num(source.small.speed, flat.smallSpeed);
    flat.smallOpacity = oldOpacity(source.small.opacity, flat.smallOpacity);
    flat.smallCentreBias = num(source.small.centreBias, num(source.small.centerBias, flat.smallCentreBias));
    flat.smallSoftness = num(source.small.softness, flat.smallSoftness);
    flat.smallMin = num(source.small.minSize, flat.smallMin);
    flat.smallMax = num(source.small.maxSize, flat.smallMax);
  }

  if (source.medium && typeof source.medium === 'object') {
    flat.mediumDiameter = num(source.medium.spreadDiameter, num(source.dots?.spreadDiameter, num(source.spreadDiameter, flat.mediumDiameter)));
    flat.mediumAmount = num(source.medium.amount, flat.mediumAmount);
    flat.mediumSpeed = num(source.medium.speed, flat.mediumSpeed);
    flat.mediumOpacity = oldOpacity(source.medium.opacity, flat.mediumOpacity);
    flat.mediumCentreBias = num(source.medium.centreBias, num(source.medium.centerBias, flat.mediumCentreBias));
    flat.mediumSoftness = num(source.medium.softness, flat.mediumSoftness);
    flat.mediumMin = num(source.medium.minSize, flat.mediumMin);
    flat.mediumMax = num(source.medium.maxSize, flat.mediumMax);
  }

  if (source.large && typeof source.large === 'object') {
    flat.largeDiameter = num(source.large.spreadDiameter, num(source.dots?.spreadDiameter, num(source.spreadDiameter, flat.largeDiameter)));
    flat.largeAmount = num(source.large.amount, flat.largeAmount);
    flat.largeSpeed = num(source.large.speed, flat.largeSpeed);
    flat.largeOpacity = oldOpacity(source.large.opacity, flat.largeOpacity);
    flat.largeCentreBias = num(source.large.centreBias, num(source.large.centerBias, flat.largeCentreBias));
    flat.largeSoftness = num(source.large.softness, flat.largeSoftness);
    flat.largeMin = num(source.large.minSize, flat.largeMin);
    flat.largeMax = num(source.large.maxSize, flat.largeMax);

    if (source.large.splatter && typeof source.large.splatter === 'object') {
      if (has(source.large.splatter, 'enabled')) flat.splatterEnabled = source.large.splatter.enabled ? 1 : 0;
      if (has(source.large.splatter, 'minLines')) flat.splatterMinLines = source.large.splatter.minLines;
      if (has(source.large.splatter, 'maxLines')) flat.splatterMaxLines = source.large.splatter.maxLines;
      if (has(source.large.splatter, 'minLength')) flat.splatterMinLength = source.large.splatter.minLength;
      if (has(source.large.splatter, 'maxLength')) flat.splatterMaxLength = source.large.splatter.maxLength;
      if (has(source.large.splatter, 'minThickness')) flat.splatterMinThickness = source.large.splatter.minThickness;
      if (has(source.large.splatter, 'maxThickness')) flat.splatterMaxThickness = source.large.splatter.maxThickness;
      if (has(source.large.splatter, 'minWidth')) flat.splatterMinThickness = source.large.splatter.minWidth;
      if (has(source.large.splatter, 'maxWidth')) flat.splatterMaxThickness = source.large.splatter.maxWidth;
    }
  }

  return flat;
}

function normaliseRendererSettings(rawSettings) {
  const source = flattenRendererSettings(rawSettings);

  if (!source) return null;

  const settings = {
    brushRotationEnabled: readNumber(source.brushRotationEnabled, 0) ? 1 : 0,
    brushRotationPointRadius: clamp(readNumber(source.brushRotationPointRadius, 18), 0, 100),
    brushRotationPointAngle: clamp(readNumber(source.brushRotationPointAngle, 52), 0, 360),
    brushRotationSpeed: clamp(readNumber(source.brushRotationSpeed, 1), -20, 20),

    dotEdgeAvoid: clamp(readNumber(source.dotEdgeAvoid, 0), 0, 100),
    dotFadeStart: clamp(readNumber(source.dotFadeStart, 0.02), 0, 1),
    dotFadeStrength: clamp(readNumber(source.dotFadeStrength, 2.5), 0, 10),

    smallDiameter: clamp(readNumber(source.smallDiameter, 147), 10, 300),
    smallAmount: clamp(readNumber(source.smallAmount, 0), 0, 30000),
    smallSpeed: clamp(readNumber(source.smallSpeed, 1), 0.1, 10),
    smallOpacity: clamp(readNumber(source.smallOpacity, 1), 0, 1),
    smallCentreBias: clamp(readNumber(source.smallCentreBias, 0), -100, 100),
    smallSoftness: clamp(readNumber(source.smallSoftness, 0), 0, 1),
    smallMin: clamp(readNumber(source.smallMin, 1), 1, 10),
    smallMax: clamp(readNumber(source.smallMax, 1), 1, 10),

    mediumDiameter: clamp(readNumber(source.mediumDiameter, 109), 10, 300),
    mediumAmount: clamp(readNumber(source.mediumAmount, 0), 0, 3000),
    mediumSpeed: clamp(readNumber(source.mediumSpeed, 1), 0.1, 10),
    mediumOpacity: clamp(readNumber(source.mediumOpacity, 1), 0, 1),
    mediumCentreBias: clamp(readNumber(source.mediumCentreBias, 0), -100, 100),
    mediumSoftness: clamp(readNumber(source.mediumSoftness, 0), 0, 1),
    mediumMin: clamp(readNumber(source.mediumMin, 1), 1, 80),
    mediumMax: clamp(readNumber(source.mediumMax, 1), 1, 80),

    largeDiameter: clamp(readNumber(source.largeDiameter, 10), 10, 300),
    largeAmount: clamp(readNumber(source.largeAmount, 0), 0, 1000),
    largeSpeed: clamp(readNumber(source.largeSpeed, 1), 0.1, 100),
    largeOpacity: clamp(readNumber(source.largeOpacity, 1), 0, 1),
    largeCentreBias: clamp(readNumber(source.largeCentreBias, 0), -100, 100),
    largeSoftness: clamp(readNumber(source.largeSoftness, 0), 0, 1),
    largeMin: clamp(readNumber(source.largeMin, 1), 1, 80),
    largeMax: clamp(readNumber(source.largeMax, 1), 1, 80),

    splatterEnabled: readNumber(source.splatterEnabled, 0) ? 1 : 0,
    splatterMinLines: clamp(Math.round(readNumber(source.splatterMinLines, 1)), 0, 20),
    splatterMaxLines: clamp(Math.round(readNumber(source.splatterMaxLines, 1)), 0, 20),
    splatterMinLength: clamp(readNumber(source.splatterMinLength, 0), 0, 120),
    splatterMaxLength: clamp(readNumber(source.splatterMaxLength, 0), 0, 120),
    splatterMinThickness: clamp(readNumber(source.splatterMinThickness, 1), 1, 20),
    splatterMaxThickness: clamp(readNumber(source.splatterMaxThickness, 1), 1, 20),
  };

  [
    ['smallMin', 'smallMax'],
    ['mediumMin', 'mediumMax'],
    ['largeMin', 'largeMax'],
    ['splatterMinLines', 'splatterMaxLines'],
    ['splatterMinLength', 'splatterMaxLength'],
    ['splatterMinThickness', 'splatterMaxThickness'],
  ].forEach(([a, b]) => {
    if (settings[a] > settings[b]) {
      const min = settings[b];
      settings[b] = settings[a];
      settings[a] = min;
    }
  });

  return settings;
}

function makeZeroPaintSettings(settings) {
  return {
    ...settings,
    smallAmount: 0,
    mediumAmount: 0,
    largeAmount: 0,
  };
}

function getAirbrushDistanceScale(options = {}) {
  return clampAirbrushDistance(options.airbrushDistance) / AIRBRUSH_BASELINE_DISTANCE;
}

function getAirbrushResolutionScale(options = {}) {
  return clamp(readNumber(options.airbrushResolutionScale, 1), 0.25, AIRBRUSH_MAX_RESOLUTION_SCALE);
}

function getAirbrushSpatialScale(options = {}) {
  const explicitDiameterScale = readNumber(options.airbrushDiameterScale, 1);
  return Math.abs(explicitDiameterScale - 1) > 0.0001
    ? Math.max(0.0001, explicitDiameterScale)
    : getAirbrushResolutionScale(options);
}

function getAirbrushDotScale(options = {}) {
  const explicitDotScale = readNumber(options.airbrushDotScale, NaN);
  return Number.isFinite(explicitDotScale)
    ? Math.max(0.0001, explicitDotScale)
    : getAirbrushResolutionScale(options);
}

function getAirbrushEmissionScale(options = {}) {
  const resolutionScale = getAirbrushResolutionScale(options);
  return clamp(Math.pow(resolutionScale, 1.35), 0.35, 4);
}

function getAirbrushRendererSpaceScale(options = {}) {
  return getAirbrushResolutionScale(options);
}

function scaleAirbrushSettingsForRenderer(settings, rendererSpaceScale = 1) {
  const scale = clamp(readNumber(rendererSpaceScale, 1), 0.25, AIRBRUSH_MAX_RESOLUTION_SCALE);

  if (!settings || Math.abs(scale - 1) <= 0.0001) return settings;

  return {
    ...settings,
    smallDiameter: readNumber(settings.smallDiameter, 0) * scale,
    mediumDiameter: readNumber(settings.mediumDiameter, 0) * scale,
    largeDiameter: readNumber(settings.largeDiameter, 0) * scale,
    smallMin: readNumber(settings.smallMin, 0) * scale,
    smallMax: readNumber(settings.smallMax, 0) * scale,
    mediumMin: readNumber(settings.mediumMin, 0) * scale,
    mediumMax: readNumber(settings.mediumMax, 0) * scale,
    largeMin: readNumber(settings.largeMin, 0) * scale,
    largeMax: readNumber(settings.largeMax, 0) * scale,
    splatterMinLength: readNumber(settings.splatterMinLength, 0) * scale,
    splatterMaxLength: readNumber(settings.splatterMaxLength, 0) * scale,
    splatterMinThickness: readNumber(settings.splatterMinThickness, 0) * scale,
    splatterMaxThickness: readNumber(settings.splatterMaxThickness, 0) * scale,
  };
}

function toAirbrushRendererSpacePoint(point, rendererSpaceScale = 1) {
  if (!point) return null;
  const scale = clamp(readNumber(rendererSpaceScale, 1), 0.25, AIRBRUSH_MAX_RESOLUTION_SCALE);
  return {
    ...point,
    x: readNumber(point.x, 0) * scale,
    y: readNumber(point.y, 0) * scale,
  };
}

function fromAirbrushRendererSpaceRect(rect, rendererSpaceScale = 1) {
  if (!isRect(rect)) return null;
  const scale = Math.max(0.0001, clamp(readNumber(rendererSpaceScale, 1), 0.25, AIRBRUSH_MAX_RESOLUTION_SCALE));
  return {
    x: rect.x / scale,
    y: rect.y / scale,
    width: rect.width / scale,
    height: rect.height / scale,
  };
}

function withContextAirbrushResolution(ctx, options = {}) {
  if (!ctx?.canvas) return options || {};
  if (Number.isFinite(Number(options?.airbrushResolutionScale))) return options || {};

  const width = Number(ctx.canvas.width) || AIRBRUSH_REFERENCE_RESOLUTION;
  const height = Number(ctx.canvas.height) || AIRBRUSH_REFERENCE_RESOLUTION;
  const resolutionScale = clamp(
    Math.max(width, height) / AIRBRUSH_REFERENCE_RESOLUTION,
    0.25,
    AIRBRUSH_MAX_RESOLUTION_SCALE,
  );

  return {
    ...(options || {}),
    airbrushResolutionScale: resolutionScale,
  };
}

function getAirbrushDotStreamSpacing(settings) {
  return DOT_STREAM_SPACING_PX * clamp(readNumber(settings?.resolutionScale, 1), 0.25, AIRBRUSH_MAX_RESOLUTION_SCALE);
}

function nextPowerOfTwo(value) {
  let result = 1;
  const target = Math.max(1, Math.ceil(value));
  while (result < target) result *= 2;
  return result;
}

function getAirbrushRendererDimensions(settings) {
  const largestRadius = Math.max(
    readNumber(settings?.smallDiameter, 0),
    readNumber(settings?.mediumDiameter, 0),
    readNumber(settings?.largeDiameter, 0),
  ) * 0.5;
  const largestDotRadius = Math.max(
    readNumber(settings?.smallMax, 0),
    readNumber(settings?.mediumMax, 0),
    readNumber(settings?.largeMax, 0),
  ) * 0.5;
  const splatterLength = readNumber(settings?.splatterMaxLength, 0);
  const requiredRadius = largestRadius + largestDotRadius + splatterLength + 32;
  const rendererSize = clamp(
    nextPowerOfTwo(requiredRadius * 2),
    AIRBRUSH_MIN_RENDERER_SIZE,
    AIRBRUSH_MAX_RENDERER_SIZE,
  );
  const edgeMargin = clamp(
    Math.ceil(requiredRadius * 0.7),
    64,
    Math.floor(rendererSize * 0.42),
  );

  return { rendererSize, edgeMargin };
}

function scaleAirbrushDiameterForDistance(value, distance) {
  const baseDiameter = readNumber(value, MIN_AIRBRUSH_SPRAY_DIAMETER);

  if (distance <= MIN_AIRBRUSH_DISTANCE) return MIN_AIRBRUSH_SPRAY_DIAMETER;

  if (distance < AIRBRUSH_BASELINE_DISTANCE) {
    const t = (distance - MIN_AIRBRUSH_DISTANCE) / (AIRBRUSH_BASELINE_DISTANCE - MIN_AIRBRUSH_DISTANCE);
    return clamp(MIN_AIRBRUSH_SPRAY_DIAMETER + ((baseDiameter - MIN_AIRBRUSH_SPRAY_DIAMETER) * t), MIN_AIRBRUSH_SPRAY_DIAMETER, MAX_AIRBRUSH_SPRAY_DIAMETER);
  }

  return clamp(baseDiameter * (distance / AIRBRUSH_BASELINE_DISTANCE), MIN_AIRBRUSH_SPRAY_DIAMETER, MAX_AIRBRUSH_SPRAY_DIAMETER);
}

function applyAirbrushDistanceToSettings(settings, options = {}) {
  if (!settings) return null;

  const distance = clampAirbrushDistance(options.airbrushDistance);
  const distanceAdjusted = Math.abs(distance - AIRBRUSH_BASELINE_DISTANCE) <= 0.0001
    ? { ...settings }
    : {
      ...settings,
      smallDiameter: scaleAirbrushDiameterForDistance(settings.smallDiameter, distance),
      mediumDiameter: scaleAirbrushDiameterForDistance(settings.mediumDiameter, distance),
      largeDiameter: scaleAirbrushDiameterForDistance(settings.largeDiameter, distance),
    };

  const resolutionScale = getAirbrushResolutionScale(options);
  const spatialScale = getAirbrushSpatialScale(options);
  const dotScale = getAirbrushDotScale(options);
  const emissionScale = options.airbrushLiteralEmissionRate === true
    ? 1
    : getAirbrushEmissionScale(options);

  return {
    ...distanceAdjusted,
    resolutionScale,
    spatialScale,
    dotScale,
    emissionScale,
    smallDiameter: distanceAdjusted.smallDiameter * spatialScale,
    mediumDiameter: distanceAdjusted.mediumDiameter * spatialScale,
    largeDiameter: distanceAdjusted.largeDiameter * spatialScale,
    smallMin: distanceAdjusted.smallMin * dotScale,
    smallMax: distanceAdjusted.smallMax * dotScale,
    mediumMin: distanceAdjusted.mediumMin * dotScale,
    mediumMax: distanceAdjusted.mediumMax * dotScale,
    largeMin: distanceAdjusted.largeMin * dotScale,
    largeMax: distanceAdjusted.largeMax * dotScale,
    splatterMinLength: distanceAdjusted.splatterMinLength * spatialScale,
    splatterMaxLength: distanceAdjusted.splatterMaxLength * spatialScale,
    splatterMinThickness: distanceAdjusted.splatterMinThickness * dotScale,
    splatterMaxThickness: distanceAdjusted.splatterMaxThickness * dotScale,
    smallAmount: distanceAdjusted.smallAmount * emissionScale,
    mediumAmount: distanceAdjusted.mediumAmount * emissionScale,
    largeAmount: distanceAdjusted.largeAmount * emissionScale,
  };
}

function getAirbrushModeSettings(options = {}) {
  const pressure = getAirbrushPressurePsi(options);
  const paintFlow = getAirbrushPaintFlowPercent(options);

  if (!isAllowedPressure(pressure)) return null;

  if (paintFlow === 0) {
    const base = normaliseRendererSettings(
      AIRBRUSH_SAVED_MODE_SETTINGS[`${pressure}:20`] || AIRBRUSH_SAVED_MODE_SETTINGS['5:20'],
    );

    return base ? applyAirbrushDistanceToSettings(makeZeroPaintSettings(base), options) : null;
  }

  if (!isAllowedPaintFlow(paintFlow)) return null;

  return applyAirbrushDistanceToSettings(
    normaliseRendererSettings(
      AIRBRUSH_SAVED_MODE_SETTINGS[getAirbrushModeKey(options)]
      || AIRBRUSH_SAVED_MODE_SETTINGS[`${pressure}:20`]
      || AIRBRUSH_SAVED_MODE_SETTINGS['5:20'],
    ),
    options,
  );
}

function getRendererSettingsHash(settings) {
  return AIRBRUSH_RENDERER_SETTING_KEYS.map((key) => `${key}:${settings[key]}`).join('|');
}

function getStateKey(options = {}, settings) {
  const strokeKey = options.strokeId || options.liveStrokeId || options.strokeKey || options.pointerId || options.pointerType || 'airbrush';

  return [
    strokeKey,
    getAirbrushModeKey(options),
    options.strokeSeed || 1,
    getRendererSettingsHash(settings),
  ].join(':');
}

function clearRendererDensityState(state) {
  if (!state) return;

  state.smallDensity.fill(0);
  state.mediumDensity.fill(0);
  state.largeDensity.fill(0);
  state.previousAlpha.fill(0);
  state.previousCenter = null;
  state.previousPaintPoint = null;
  state.gpuPreviousCenter = null;
  state.gpuPreviousPaintPoint = null;
  state.gpuEmissionRemainders = { small: 0, medium: 0, large: 0 };
  state.brushRotationAngle = 0;
  state.brushRotationDirection = 1;
  state.brushRotationDirectionElapsed = 0;
}

function ensureRendererOrigin(state, pointValue) {
  if (!state || !pointValue) return;

  if (!Number.isFinite(state.originX) || !Number.isFinite(state.originY)) {
    state.originX = Math.round(pointValue.x - (state.rendererSize / 2));
    state.originY = Math.round(pointValue.y - (state.rendererSize / 2));
    return;
  }

  const localX = pointValue.x - state.originX;
  const localY = pointValue.y - state.originY;

  if (
    localX < state.edgeMargin
    || localY < state.edgeMargin
    || localX > state.rendererSize - state.edgeMargin
    || localY > state.rendererSize - state.edgeMargin
  ) {
    clearRendererDensityState(state);
    state.originX = Math.round(pointValue.x - (state.rendererSize / 2));
    state.originY = Math.round(pointValue.y - (state.rendererSize / 2));
  }
}

function trimRenderState(now = Date.now()) {
  airbrushRenderState.forEach((state, key) => {
    if (!state || !state.lastUsedAt || now - state.lastUsedAt > AIRBRUSH_STATE_TTL_MS) {
      airbrushRenderState.delete(key);
    }
  });

  if (airbrushRenderState.size <= AIRBRUSH_STATE_LIMIT) return;

  const entries = Array.from(airbrushRenderState.entries()).sort((a, b) => (a[1].lastUsedAt || 0) - (b[1].lastUsedAt || 0));
  const removeCount = Math.max(1, entries.length - AIRBRUSH_STATE_LIMIT);

  for (let i = 0; i < removeCount; i += 1) {
    airbrushRenderState.delete(entries[i][0]);
  }
}

function createRenderState(key, settings, options = {}) {
  const patchCanvas = createCanvas(1, 1);

  if (!patchCanvas) return null;

  const defaultDimensions = getAirbrushRendererDimensions(settings);
  const requestedRendererSize = Number(options.airbrushRendererSize);
  const rendererSize = Number.isFinite(requestedRendererSize)
    ? clamp(Math.ceil(requestedRendererSize), AIRBRUSH_MIN_RENDERER_SIZE, AIRBRUSH_MAX_RENDERER_SIZE)
    : defaultDimensions.rendererSize;
  const edgeMargin = Math.min(defaultDimensions.edgeMargin, Math.floor(rendererSize * 0.42));
  const totalPixels = rendererSize * rendererSize;

  return {
    key,
    patchCanvas,
    rendererSize,
    edgeMargin,
    smallDensity: new Float32Array(totalPixels),
    mediumDensity: new Float32Array(totalPixels),
    largeDensity: new Float32Array(totalPixels),
    previousAlpha: new Float32Array(totalPixels),
    originX: null,
    originY: null,
    lastUsedAt: Date.now(),
    brushRotationAngle: 0,
    brushRotationDirection: 1,
    brushRotationDirectionElapsed: 0,
    previousCenter: null,
    previousPaintPoint: null,
    gpuEmissionRemainders: { small: 0, medium: 0, large: 0 },
  };
}

function getRenderState(options = {}, settings) {
  const key = getStateKey(options, settings);
  const now = Date.now();

  trimRenderState(now);

  const existing = airbrushRenderState.get(key);

  if (existing) {
    existing.lastUsedAt = now;
    return existing;
  }

  const state = createRenderState(key, settings, options);

  if (!state) return null;

  airbrushRenderState.set(key, state);
  trimRenderState(now);

  return state;
}

function rectClamp(r, w, h) {
  if (!isRect(r)) return null;

  const x = clamp(Math.floor(r.x), 0, w);
  const y = clamp(Math.floor(r.y), 0, h);
  const right = clamp(Math.ceil(r.x + r.width), 0, w);
  const bottom = clamp(Math.ceil(r.y + r.height), 0, h);

  return right > x && bottom > y
    ? { x, y, width: right - x, height: bottom - y }
    : null;
}

function getDotField(settings, type = 'small') {
  const diameter = {
    small: settings.smallDiameter,
    medium: settings.mediumDiameter,
    large: settings.largeDiameter,
  }[type] || settings.smallDiameter;

  return {
    baseR: clamp(diameter, MIN_AIRBRUSH_SPRAY_DIAMETER, MAX_AIRBRUSH_SPRAY_DIAMETER) / 2,
  };
}

function getLargestDotField(settings) {
  const baseR = Math.max(
    getDotField(settings, 'small').baseR,
    getDotField(settings, 'medium').baseR,
    getDotField(settings, 'large').baseR,
  );

  return { baseR };
}

function sampleCentreBiasDistance(centreBias) {
  const bias = clamp(centreBias, -100, 100);
  const amount = Math.abs(bias) / 100;

  if (bias >= 100) return 0;
  if (bias <= -100) return 1;
  if (amount <= 0.0001) return Math.sqrt(Math.random());

  const evenDistanceNorm = Math.sqrt(Math.random());

  if (Math.random() > amount) return evenDistanceNorm;
  if (bias > 0) return 1 - Math.sqrt(Math.random());

  return Math.sqrt(Math.random() * (2 - Math.random()));
}

function applySmallEdgeAvoid(settings, distanceNorm) {
  const avoidChance = clamp(settings.dotEdgeAvoid, 0, 100) / 100;
  const fadeStart = clamp(settings.dotFadeStart, 0, 1);

  if (avoidChance <= 0) return distanceNorm;
  if (distanceNorm < fadeStart) return distanceNorm;
  if (Math.random() >= avoidChance) return distanceNorm;

  return Math.sqrt(Math.random()) * fadeStart;
}

function getSmallEdgeFadeOpacity(settings, distanceNorm) {
  const fadeStart = clamp(settings.dotFadeStart, 0, 1);
  const fadeStrength = clamp(settings.dotFadeStrength, 0, 10);

  if (fadeStrength <= 0) return 1;
  if (fadeStart >= 1) return 1;
  if (distanceNorm <= fadeStart) return 1;

  const t = clamp((distanceNorm - fadeStart) / Math.max(0.0001, 1 - fadeStart), 0, 1);
  return Math.pow(1 - t, fadeStrength);
}

function getSprayOffset(settings, radius, centreBias, type = 'small') {
  const angle = Math.random() * Math.PI * 2;
  const rawDistanceNorm = sampleCentreBiasDistance(centreBias);
  const distanceNorm = type === 'small' ? applySmallEdgeAvoid(settings, rawDistanceNorm) : rawDistanceNorm;
  const distance = radius * clamp(distanceNorm, 0, 1);

  return {
    x: Math.cos(angle) * distance,
    y: Math.sin(angle) * distance,
    distanceNorm,
  };
}

function dotCircle(buf, w, h, cx, cy, size, strength = 0.34, dotSoftness = 0) {
  const softness = clamp(dotSoftness, 0, 1);
  const coreRadius = Math.max(0.1, size / 2);
  const blurRadius = coreRadius * (1 + softness * 4);
  const pixelRadius = Math.max(1, Math.ceil(blurRadius));

  for (let y = -pixelRadius; y <= pixelRadius; y += 1) {
    for (let x = -pixelRadius; x <= pixelRadius; x += 1) {
      const distance = Math.hypot(x, y);

      if (distance > blurRadius) continue;

      const px = Math.round(cx + x);
      const py = Math.round(cy + y);

      if (px < 0 || py < 0 || px >= w || py >= h) continue;

      const hardN = distance / coreRadius;
      const softN = distance / blurRadius;
      const hardAlpha = hardN <= 1 ? 1 : 0;
      const softAlpha = Math.pow(1 - smoothstep(0, 1, softN), 1.55);
      const falloff = softness > 0.001 ? hardAlpha * (1 - softness) + softAlpha * softness : hardAlpha;
      const alpha = clamp(strength * falloff, 0, 1);
      const idx = py * w + px;

      buf[idx] = buf[idx] + alpha * (1 - buf[idx]);
    }
  }

  return {
    x: cx - pixelRadius - 1,
    y: cy - pixelRadius - 1,
    width: pixelRadius * 2 + 3,
    height: pixelRadius * 2 + 3,
  };
}

const SPLATTER_RENDER_LENGTH_SCALE = 0.5;
const SPLATTER_RENDER_THICKNESS_SCALE = 0.5;

function getSplatterThicknessGradient(t) {
  const progress = clamp(readNumber(t, 0), 0, 1);

  if (progress <= 0.2) {
    return 2 - (progress / 0.2) * 0.5;
  }

  if (progress <= 0.5) {
    return 1.5 - ((progress - 0.2) / 0.3) * 0.5;
  }

  return 1;
}

function getSplatterLineSteps(length, thickness) {
  const spacing = Math.max(0.12, Math.min(0.35, thickness * 0.18));
  return Math.max(1, Math.ceil(Math.max(0.001, length) / spacing));
}

function splatterLine(state, settings, x1, y1, x2, y2, thickness, w, h) {
  const length = Math.max(0.001, Math.hypot(x2 - x1, y2 - y1));
  const steps = getSplatterLineSteps(length, thickness);
  let out = null;

  for (let i = 0; i <= steps; i += 1) {
    const t = i / steps;
    const taperedThickness = Math.max(0.5, thickness * getSplatterThicknessGradient(t));

    out = merge(out, dotCircle(
      state.largeDensity,
      w,
      h,
      x1 + (x2 - x1) * t,
      y1 + (y2 - y1) * t,
      taperedThickness,
      0.24 * (1 - t * 0.55),
      0,
    ));
  }

  return out;
}

function depositSplatter(state, settings, cx, cy, dotSize, w, h, dirty) {
  if (!settings.splatterEnabled) return dirty;

  const minLines = Math.max(0, Math.min(settings.splatterMinLines, settings.splatterMaxLines));
  const maxLines = Math.max(0, Math.max(settings.splatterMinLines, settings.splatterMaxLines));
  const minLength = Math.max(0, Math.min(settings.splatterMinLength, settings.splatterMaxLength));
  const maxLength = Math.max(0, Math.max(settings.splatterMinLength, settings.splatterMaxLength));
  const minThickness = Math.max(1, Math.min(settings.splatterMinThickness, settings.splatterMaxThickness));
  const maxThickness = Math.max(1, Math.max(settings.splatterMinThickness, settings.splatterMaxThickness));
  let out = dirty;

  for (let i = 0; i < randInt(minLines, maxLines); i += 1) {
    const angle = Math.random() * Math.PI * 2;
    const start = Math.max(0.1, dotSize * 0.32);
    const length = rand(minLength, maxLength) * SPLATTER_RENDER_LENGTH_SCALE;
    const thickness = rand(minThickness, maxThickness) * SPLATTER_RENDER_THICKNESS_SCALE;

    out = merge(out, splatterLine(
      state,
      settings,
      cx + Math.cos(angle) * start,
      cy + Math.sin(angle) * start,
      cx + Math.cos(angle) * (start + length),
      cy + Math.sin(angle) * (start + length),
      thickness,
      w,
      h,
    ));
  }

  return out;
}

function depositDots(type, state, settings, cx, cy, dt, w, h, dirty) {
  const cfg = {
    small: {
      amount: settings.smallAmount,
      speed: settings.smallSpeed,
      min: settings.smallMin,
      max: settings.smallMax,
      opacity: settings.smallOpacity,
      centreBias: settings.smallCentreBias,
      softness: settings.smallSoftness,
      buf: state.smallDensity,
    },
    medium: {
      amount: settings.mediumAmount,
      speed: settings.mediumSpeed,
      min: settings.mediumMin,
      max: settings.mediumMax,
      opacity: settings.mediumOpacity,
      centreBias: settings.mediumCentreBias,
      softness: settings.mediumSoftness,
      buf: state.mediumDensity,
    },
    large: {
      amount: settings.largeAmount,
      speed: settings.largeSpeed,
      min: settings.largeMin,
      max: settings.largeMax,
      opacity: settings.largeOpacity,
      centreBias: settings.largeCentreBias,
      softness: settings.largeSoftness,
      buf: state.largeDensity,
    },
  }[type];

  if (!cfg || cfg.amount <= 0) return dirty;

  const { baseR } = getDotField(settings, type);
  const min = Math.max(1, Math.min(cfg.min, cfg.max));
  const max = Math.max(1, Math.max(cfg.min, cfg.max));
  const speed = clamp(cfg.speed, 0.1, type === 'large' ? 100 : 10);
  const count = Math.floor(Math.max(0, cfg.amount) * speed * dt + Math.random());
  const extra = type === 'small' ? max + 2 : max + (type === 'large' ? settings.splatterMaxLength : 2);
  let out = merge(dirty, {
    x: cx - baseR - extra - 2,
    y: cy - baseR - extra - 2,
    width: baseR * 2 + extra * 2 + 5,
    height: baseR * 2 + extra * 2 + 5,
  });

  for (let i = 0; i < count; i += 1) {
    const offset = getSprayOffset(settings, baseR, cfg.centreBias, type);
    const x = cx + offset.x;
    const y = cy + offset.y;
    const size = rand(min, max);
    const edgeOpacity = type === 'small' ? getSmallEdgeFadeOpacity(settings, offset.distanceNorm) : 1;
    const alpha = clamp(cfg.opacity, 0, 1) * edgeOpacity;

    out = merge(out, dotCircle(cfg.buf, w, h, x, y, size, alpha, cfg.softness));

    if (type === 'large') {
      out = depositSplatter(state, settings, x, y, size, w, h, out);
    }
  }

  return out;
}

function depositDotSpray(state, settings, x, y, dt, w, h) {
  const { baseR } = getLargestDotField(settings);

  let dirty = {
    x: x - baseR - 140,
    y: y - baseR - 140,
    width: baseR * 2 + 285,
    height: baseR * 2 + 285,
  };

  dirty = depositDots('small', state, settings, x, y, dt, w, h, dirty);
  dirty = depositDots('medium', state, settings, x, y, dt, w, h, dirty);
  return depositDots('large', state, settings, x, y, dt, w, h, dirty);
}

function depositDotStream(state, settings, from, to, dt, w, h) {
  const distance = Math.hypot(to.x - from.x, to.y - from.y);

  if (distance < 0.001) return depositDotSpray(state, settings, to.x, to.y, dt, w, h);

  const steps = Math.max(1, Math.ceil(distance / getAirbrushDotStreamSpacing(settings)));
  const sampleDt = dt / steps;
  let dirty = null;

  for (let i = 1; i <= steps; i += 1) {
    const t = i / steps;

    dirty = merge(dirty, depositDotSpray(
      state,
      settings,
      from.x + (to.x - from.x) * t,
      from.y + (to.y - from.y) * t,
      sampleDt,
      w,
      h,
    ));
  }

  return dirty;
}

function getPaintPointAt(point, settings, angle) {
  if (!point || !settings.brushRotationEnabled) return point;

  const { baseR } = getLargestDotField(settings);
  const offsetDistance = baseR * clamp(settings.brushRotationPointRadius, 0, 100) / 100;
  const offsetAngle = (settings.brushRotationPointAngle * Math.PI / 180) + angle;

  return {
    x: point.x + Math.cos(offsetAngle) * offsetDistance,
    y: point.y + Math.sin(offsetAngle) * offsetDistance,
  };
}

function getBrushRotationDelta(state, settings, dt) {
  if (!settings.brushRotationEnabled) return 0;

  const speed = clamp(settings.brushRotationSpeed, -20, 20);
  let remaining = dt;
  let delta = 0;

  while (remaining > 0) {
    const untilChange = BRUSH_ROTATION_DIRECTION_SECONDS - state.brushRotationDirectionElapsed;
    const chunk = Math.min(remaining, untilChange);

    delta += state.brushRotationDirection * speed * Math.PI * 2 * chunk;
    state.brushRotationDirectionElapsed += chunk;
    remaining -= chunk;

    if (state.brushRotationDirectionElapsed >= BRUSH_ROTATION_DIRECTION_SECONDS - 0.000001) {
      state.brushRotationDirection = Math.random() < 0.5 ? -1 : 1;
      state.brushRotationDirectionElapsed = 0;
    }
  }

  return delta;
}

function depositRotatingDots(state, settings, fromCenter, toCenter, fromAngle, toAngle, dt, w, h) {
  const angleDistance = Math.abs(toAngle - fromAngle);
  const centerDistance = fromCenter ? Math.hypot(toCenter.x - fromCenter.x, toCenter.y - fromCenter.y) : 0;
  const steps = Math.max(1, Math.ceil(angleDistance / (Math.PI / 64)), Math.ceil(centerDistance / getAirbrushDotStreamSpacing(settings)));
  let dirty = null;
  let previousPaintPoint = state.previousPaintPoint;

  for (let i = 1; i <= steps; i += 1) {
    const t = i / steps;
    const center = fromCenter
      ? {
        x: fromCenter.x + (toCenter.x - fromCenter.x) * t,
        y: fromCenter.y + (toCenter.y - fromCenter.y) * t,
      }
      : toCenter;
    const angle = fromAngle + (toAngle - fromAngle) * t;
    const paintPoint = getPaintPointAt(center, settings, angle);

    dirty = previousPaintPoint
      ? merge(dirty, depositDotStream(state, settings, previousPaintPoint, paintPoint, dt / steps, w, h))
      : merge(dirty, depositDotSpray(state, settings, paintPoint.x, paintPoint.y, dt / steps, w, h));

    previousPaintPoint = paintPoint;
  }

  state.previousPaintPoint = previousPaintPoint;
  return dirty;
}


function createShader(gl, type, source) {
  const shader = gl.createShader(type);

  if (!shader) return null;

  gl.shaderSource(shader, source);
  gl.compileShader(shader);

  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    gl.deleteShader(shader);
    return null;
  }

  return shader;
}

function createProgram(gl, vertexSource, fragmentSource) {
  const vertexShader = createShader(gl, gl.VERTEX_SHADER, vertexSource);
  const fragmentShader = createShader(gl, gl.FRAGMENT_SHADER, fragmentSource);

  if (!vertexShader || !fragmentShader) return null;

  const program = gl.createProgram();

  if (!program) return null;

  gl.attachShader(program, vertexShader);
  gl.attachShader(program, fragmentShader);
  gl.linkProgram(program);
  gl.deleteShader(vertexShader);
  gl.deleteShader(fragmentShader);

  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    gl.deleteProgram(program);
    return null;
  }

  return program;
}

function ensureGpuAirbrushRenderer(state) {
  if (!state) return null;

  if (state.gpuAirbrushFailed) return null;

  if (state.gpuAirbrush && state.gpuAirbrush.gl && !state.gpuAirbrush.gl.isContextLost()) {
    const surface = getSharedCanvasToolWebglSurface(state.rendererSize, state.rendererSize);
    if (surface && surface.canvas) {
      state.gpuAirbrush.canvas = surface.canvas;
      state.gpuAirbrush.gl = surface.gl;
      state.gpuAirbrush.size = state.rendererSize;
    }
    return state.gpuAirbrush;
  }

  const surface = getSharedCanvasToolWebglSurface(state.rendererSize, state.rendererSize);
  const canvas = surface && surface.canvas;
  const gl = surface && surface.gl;

  if (!canvas || !gl) {
    state.gpuAirbrushFailed = true;
    return null;
  }

  const vertexSource = `
    attribute vec2 a_center;
    attribute vec2 a_corner;
    attribute float a_radius;
    attribute float a_alpha;
    attribute float a_softness;
    uniform vec2 u_resolution;
    varying vec2 v_corner;
    varying float v_alpha;
    varying float v_softness;
    void main() {
      vec2 position = a_center + (a_corner * a_radius);
      vec2 zeroToOne = position / u_resolution;
      vec2 clipSpace = (zeroToOne * 2.0) - 1.0;
      gl_Position = vec4(clipSpace.x, -clipSpace.y, 0.0, 1.0);
      v_corner = a_corner;
      v_alpha = a_alpha;
      v_softness = a_softness;
    }
  `;
  const fragmentSource = `
    precision mediump float;
    uniform vec3 u_color;
    varying vec2 v_corner;
    varying float v_alpha;
    varying float v_softness;
    void main() {
      float dist = length(v_corner);
      if (dist > 1.0) discard;
      float softness = clamp(v_softness, 0.0, 0.98);
      float edge = softness <= 0.001 ? 1.0 : 1.0 - smoothstep(1.0 - softness, 1.0, dist);
      float alpha = clamp(v_alpha * edge, 0.0, 1.0);
      gl_FragColor = vec4(u_color * alpha, alpha);
    }
  `;
  const program = createProgram(gl, vertexSource, fragmentSource);

  if (!program) {
    state.gpuAirbrushFailed = true;
    return null;
  }

  const buffer = gl.createBuffer();

  if (!buffer) {
    state.gpuAirbrushFailed = true;
    return null;
  }

  const renderer = {
    canvas,
    gl,
    program,
    buffer,
    centerLocation: gl.getAttribLocation(program, 'a_center'),
    cornerLocation: gl.getAttribLocation(program, 'a_corner'),
    radiusLocation: gl.getAttribLocation(program, 'a_radius'),
    alphaLocation: gl.getAttribLocation(program, 'a_alpha'),
    softnessLocation: gl.getAttribLocation(program, 'a_softness'),
    resolutionLocation: gl.getUniformLocation(program, 'u_resolution'),
    colorLocation: gl.getUniformLocation(program, 'u_color'),
    size: state.rendererSize,
  };

  gl.viewport(0, 0, state.rendererSize, state.rendererSize);
  gl.disable(gl.DEPTH_TEST);
  gl.disable(gl.STENCIL_TEST);
  gl.enable(gl.BLEND);
  gl.blendFuncSeparate(gl.ONE, gl.ONE_MINUS_SRC_ALPHA, gl.ONE, gl.ONE_MINUS_SRC_ALPHA);

  state.gpuAirbrush = renderer;
  return renderer;
}


function ensureGpuAirbrushRenderTarget(renderer) {
  if (!renderer || !renderer.gl) return false;

  const gl = renderer.gl;
  const size = renderer.size;

  if (
    renderer.framebuffer
    && renderer.renderTexture
    && renderer.renderTargetSize === size
  ) {
    return true;
  }

  if (renderer.framebuffer) {
    gl.deleteFramebuffer(renderer.framebuffer);
    renderer.framebuffer = null;
  }

  if (renderer.renderTexture) {
    gl.deleteTexture(renderer.renderTexture);
    renderer.renderTexture = null;
  }

  const texture = gl.createTexture();
  const framebuffer = gl.createFramebuffer();

  if (!texture || !framebuffer) {
    if (texture) gl.deleteTexture(texture);
    if (framebuffer) gl.deleteFramebuffer(framebuffer);
    return false;
  }

  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texImage2D(
    gl.TEXTURE_2D,
    0,
    gl.RGBA,
    size,
    size,
    0,
    gl.RGBA,
    gl.UNSIGNED_BYTE,
    null,
  );

  gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer);
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0);

  const complete = gl.checkFramebufferStatus(gl.FRAMEBUFFER) === gl.FRAMEBUFFER_COMPLETE;
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  gl.bindTexture(gl.TEXTURE_2D, null);

  if (!complete) {
    gl.deleteTexture(texture);
    gl.deleteFramebuffer(framebuffer);
    return false;
  }

  renderer.renderTexture = texture;
  renderer.framebuffer = framebuffer;
  renderer.renderTargetSize = size;
  return true;
}


function clearGpuAirbrushPreviewSurface(renderer) {
  if (!renderer || !renderer.gl) return false;
  const gl = renderer.gl;
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  gl.viewport(0, 0, renderer.size, renderer.size);
  gl.disable(gl.DEPTH_TEST);
  gl.disable(gl.STENCIL_TEST);
  gl.enable(gl.BLEND);
  gl.blendFuncSeparate(gl.ONE, gl.ONE_MINUS_SRC_ALPHA, gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
  gl.clearColor(0, 0, 0, 0);
  gl.clear(gl.COLOR_BUFFER_BIT);
  return true;
}

function renderGpuAirbrushVerticesToPreviewSurface(state, renderer, vertices, colour) {
  if (!state || !renderer || !renderer.gl || !vertices || vertices.length <= 0) return false;

  const gl = renderer.gl;
  const size = state.rendererSize;
  const data = new Float32Array(vertices);
  const stride = AIRBRUSH_GPU_FLOATS_PER_VERTEX * 4;

  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  gl.viewport(0, 0, size, size);
  gl.useProgram(renderer.program);
  gl.uniform2f(renderer.resolutionLocation, size, size);
  gl.uniform3f(renderer.colorLocation, colour.r / 255, colour.g / 255, colour.b / 255);
  gl.bindBuffer(gl.ARRAY_BUFFER, renderer.buffer);
  gl.bufferData(gl.ARRAY_BUFFER, data, gl.STREAM_DRAW);

  gl.enableVertexAttribArray(renderer.centerLocation);
  gl.vertexAttribPointer(renderer.centerLocation, 2, gl.FLOAT, false, stride, 0);
  gl.enableVertexAttribArray(renderer.cornerLocation);
  gl.vertexAttribPointer(renderer.cornerLocation, 2, gl.FLOAT, false, stride, 2 * 4);
  gl.enableVertexAttribArray(renderer.radiusLocation);
  gl.vertexAttribPointer(renderer.radiusLocation, 1, gl.FLOAT, false, stride, 4 * 4);
  gl.enableVertexAttribArray(renderer.alphaLocation);
  gl.vertexAttribPointer(renderer.alphaLocation, 1, gl.FLOAT, false, stride, 5 * 4);
  gl.enableVertexAttribArray(renderer.softnessLocation);
  gl.vertexAttribPointer(renderer.softnessLocation, 1, gl.FLOAT, false, stride, 6 * 4);

  gl.disable(gl.DEPTH_TEST);
  gl.disable(gl.STENCIL_TEST);
  gl.enable(gl.BLEND);
  gl.blendFuncSeparate(gl.ONE, gl.ONE_MINUS_SRC_ALPHA, gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
  gl.drawArrays(gl.TRIANGLES, 0, data.length / AIRBRUSH_GPU_FLOATS_PER_VERTEX);
  return true;
}

function commitGpuAirbrushFramebufferPatch(ctx, renderer, r, targetX, targetY, options = {}) {
  if (!ctx || !renderer || !renderer.gl || !r) return false;

  const rendererSpaceScale = getAirbrushRendererSpaceScale(options);

  const gl = renderer.gl;
  const width = Math.max(1, r.width | 0);
  const height = Math.max(1, r.height | 0);
  const pixelCount = width * height * 4;

  if (!renderer.readBuffer || renderer.readBuffer.length !== pixelCount) {
    renderer.readBuffer = new Uint8Array(pixelCount);
  }

  try {
    gl.readPixels(r.x, renderer.size - r.y - r.height, width, height, gl.RGBA, gl.UNSIGNED_BYTE, renderer.readBuffer);
  } catch {
    return false;
  }

  if (!renderer.transferCanvas && typeof document !== 'undefined') {
    renderer.transferCanvas = document.createElement('canvas');
  }

  if (!renderer.transferCanvas) return false;

  if (renderer.transferCanvas.width !== width) renderer.transferCanvas.width = width;
  if (renderer.transferCanvas.height !== height) renderer.transferCanvas.height = height;

  if (!renderer.transferCtx) {
    renderer.transferCtx = renderer.transferCanvas.getContext('2d', { willReadFrequently: true });
  }

  if (!renderer.transferCtx) return false;

  if (
    !renderer.transferImageData
    || renderer.transferImageData.width !== width
    || renderer.transferImageData.height !== height
  ) {
    renderer.transferImageData = renderer.transferCtx.createImageData(width, height);
  }

  const imageData = renderer.transferImageData;
  const output = imageData.data;
  const input = renderer.readBuffer;
  const rowStride = width * 4;

  for (let y = 0; y < height; y += 1) {
    let sourceIndex = (height - 1 - y) * rowStride;
    let targetIndex = y * rowStride;
    const targetEnd = targetIndex + rowStride;

    while (targetIndex < targetEnd) {
      const alpha = input[sourceIndex + 3];
      output[targetIndex + 3] = alpha;

      if (alpha !== 0) {
        const scale = AIRBRUSH_UNPREMULTIPLY_SCALE[alpha];
        output[targetIndex] = Math.min(255, Math.round(input[sourceIndex] * scale));
        output[targetIndex + 1] = Math.min(255, Math.round(input[sourceIndex + 1] * scale));
        output[targetIndex + 2] = Math.min(255, Math.round(input[sourceIndex + 2] * scale));
      } else {
        output[targetIndex] = 0;
        output[targetIndex + 1] = 0;
        output[targetIndex + 2] = 0;
      }

      sourceIndex += 4;
      targetIndex += 4;
    }
  }

  renderer.transferCtx.putImageData(imageData, 0, 0);

  ctx.save();
  ctx.globalCompositeOperation = options.isEraser ? 'destination-out' : 'source-over';
  ctx.globalAlpha = clamp(num(options.opacity, 1), 0, 1);
  ctx.filter = 'none';
  ctx.shadowBlur = 0;
  ctx.drawImage(
    renderer.transferCanvas,
    0,
    0,
    width,
    height,
    targetX / rendererSpaceScale,
    targetY / rendererSpaceScale,
    width / rendererSpaceScale,
    height / rendererSpaceScale,
  );
  ctx.restore();

  return true;
}

function pushGpuAirbrushDot(vertices, dot, dirty) {
  if (!vertices || !dot) return dirty;

  const x = readNumber(dot.x, 0);
  const y = readNumber(dot.y, 0);
  const radius = Math.max(0.5, readNumber(dot.radius, 0.5));
  const alpha = clamp(readNumber(dot.alpha, 1), 0, 1);
  const softness = clamp(readNumber(dot.softness, 0), 0, 0.98);

  if (alpha <= 0.0001 || radius <= 0) return dirty;

  const corners = [
    -1, -1,
    1, -1,
    -1, 1,
    -1, 1,
    1, -1,
    1, 1,
  ];

  for (let i = 0; i < corners.length; i += 2) {
    vertices.push(x, y, corners[i], corners[i + 1], radius, alpha, softness);
  }

  return merge(dirty, {
    x: x - radius - 2,
    y: y - radius - 2,
    width: (radius * 2) + 4,
    height: (radius * 2) + 4,
  });
}

function collectGpuAirbrushDots(type, state, settings, cx, cy, dt, vertices, dirty, exactEmission = false, exactEmissionMultiplier = 1) {
  const cfg = {
    small: {
      amount: settings.smallAmount,
      speed: settings.smallSpeed,
      min: settings.smallMin,
      max: settings.smallMax,
      opacity: settings.smallOpacity,
      centreBias: settings.smallCentreBias,
      softness: settings.smallSoftness,
    },
    medium: {
      amount: settings.mediumAmount,
      speed: settings.mediumSpeed,
      min: settings.mediumMin,
      max: settings.mediumMax,
      opacity: settings.mediumOpacity,
      centreBias: settings.mediumCentreBias,
      softness: settings.mediumSoftness,
    },
    large: {
      amount: settings.largeAmount,
      speed: settings.largeSpeed,
      min: settings.largeMin,
      max: settings.largeMax,
      opacity: settings.largeOpacity,
      centreBias: settings.largeCentreBias,
      softness: settings.largeSoftness,
    },
  }[type];

  if (!cfg || cfg.amount <= 0) return dirty;

  const { baseR } = getDotField(settings, type);
  const min = Math.max(1, Math.min(cfg.min, cfg.max));
  const max = Math.max(1, Math.max(cfg.min, cfg.max));
  const speed = clamp(cfg.speed, 0.1, type === 'large' ? 100 : 10);
  // In exact 3D emission mode, `amount` is already dots per second.
  // `speed` controls the legacy renderer's density only and must not multiply
  // the literal particle rate.
  const safeExactEmissionMultiplier = exactEmission
    ? Math.max(0, Number(exactEmissionMultiplier) || 1)
    : 1;
  const requestedDots = Math.max(0, cfg.amount)
    * (exactEmission ? safeExactEmissionMultiplier : speed)
    * dt;
  let count;

  if (exactEmission && state) {
    if (!state.gpuEmissionRemainders) {
      state.gpuEmissionRemainders = { small: 0, medium: 0, large: 0 };
    }
    const accumulated = Math.max(0, Number(state.gpuEmissionRemainders[type]) || 0) + requestedDots;
    count = Math.min(AIRBRUSH_GPU_MAX_DOTS_PER_PASS, Math.floor(accumulated));
    state.gpuEmissionRemainders[type] = accumulated - count;
  } else {
    count = Math.min(AIRBRUSH_GPU_MAX_DOTS_PER_PASS, Math.floor(requestedDots + Math.random()));
  }

  if (exactEmission && state?.gpuEmissionDebugCounts) {
    state.gpuEmissionDebugCounts[type] += count;
  }

  let out = dirty;

  for (let i = 0; i < count; i += 1) {
    const offset = getSprayOffset(settings, baseR, cfg.centreBias, type);
    const x = cx + offset.x;
    const y = cy + offset.y;
    const size = rand(min, max);
    const edgeOpacity = type === 'small' ? getSmallEdgeFadeOpacity(settings, offset.distanceNorm) : 1;
    const alpha = clamp(cfg.opacity, 0, 1) * edgeOpacity;

    out = pushGpuAirbrushDot(vertices, {
      x,
      y,
      radius: size * 0.5,
      alpha,
      softness: cfg.softness,
    }, out);

    if (type === 'large' && settings.splatterEnabled) {
      const minLines = Math.round(Math.min(settings.splatterMinLines, settings.splatterMaxLines));
      const maxLines = Math.round(Math.max(settings.splatterMinLines, settings.splatterMaxLines));
      const lines = Math.round(rand(minLines, maxLines));
      const minLength = Math.min(settings.splatterMinLength, settings.splatterMaxLength);
      const maxLength = Math.max(settings.splatterMinLength, settings.splatterMaxLength);
      const minThickness = Math.min(settings.splatterMinThickness, settings.splatterMaxThickness);
      const maxThickness = Math.max(settings.splatterMinThickness, settings.splatterMaxThickness);

      for (let j = 0; j < lines; j += 1) {
        const angle = Math.random() * Math.PI * 2;
        const length = rand(minLength, maxLength) * SPLATTER_RENDER_LENGTH_SCALE;
        const thickness = rand(minThickness, maxThickness) * SPLATTER_RENDER_THICKNESS_SCALE;
        const steps = getSplatterLineSteps(length, thickness);
        const start = Math.max(0.1, size * 0.32);

        for (let k = 0; k <= steps; k += 1) {
          const t = k / steps;
          out = pushGpuAirbrushDot(vertices, {
            x: x + Math.cos(angle) * (start + (length * t)),
            y: y + Math.sin(angle) * (start + (length * t)),
            radius: Math.max(0.25, thickness * getSplatterThicknessGradient(t) * 0.5),
            alpha: alpha * (1 - (t * 0.65)),
            softness: 0,
          }, out);
        }
      }
    }
  }

  return out;
}

function collectGpuAirbrushSpray(state, settings, x, y, dt, vertices, dirty, exactEmission = false, exactEmissionMultiplier = 1) {
  let out = dirty;

  out = collectGpuAirbrushDots('small', state, settings, x, y, dt, vertices, out, exactEmission, exactEmissionMultiplier);
  out = collectGpuAirbrushDots('medium', state, settings, x, y, dt, vertices, out, exactEmission, exactEmissionMultiplier);
  out = collectGpuAirbrushDots('large', state, settings, x, y, dt, vertices, out, exactEmission, exactEmissionMultiplier);

  return out;
}

function collectGpuAirbrushStream(state, settings, from, to, dt, vertices, dirty, exactEmission = false, exactEmissionMultiplier = 1) {
  const distance = Math.hypot(to.x - from.x, to.y - from.y);

  if (distance < 0.001) return collectGpuAirbrushSpray(state, settings, to.x, to.y, dt, vertices, dirty, exactEmission, exactEmissionMultiplier);

  const steps = Math.max(1, Math.ceil(distance / getAirbrushDotStreamSpacing(settings)));
  const sampleDt = dt / steps;
  let out = dirty;

  for (let i = 1; i <= steps; i += 1) {
    const t = i / steps;

    out = collectGpuAirbrushSpray(
      state,
      settings,
      from.x + ((to.x - from.x) * t),
      from.y + ((to.y - from.y) * t),
      sampleDt,
      vertices,
      out,
      exactEmission,
      exactEmissionMultiplier,
    );
  }

  return out;
}

function collectGpuRotatingAirbrushDots(state, settings, fromCenter, toCenter, fromAngle, toAngle, dt, vertices, dirty, exactEmission = false, exactEmissionMultiplier = 1) {
  const angleDistance = Math.abs(toAngle - fromAngle);
  const centerDistance = fromCenter ? Math.hypot(toCenter.x - fromCenter.x, toCenter.y - fromCenter.y) : 0;
  const steps = Math.max(1, Math.ceil(angleDistance / (Math.PI / 64)), Math.ceil(centerDistance / getAirbrushDotStreamSpacing(settings)));
  let out = dirty;
  let previousPaintPoint = state.gpuPreviousPaintPoint;

  for (let i = 1; i <= steps; i += 1) {
    const t = i / steps;
    const center = fromCenter
      ? {
        x: fromCenter.x + ((toCenter.x - fromCenter.x) * t),
        y: fromCenter.y + ((toCenter.y - fromCenter.y) * t),
      }
      : toCenter;
    const angle = fromAngle + ((toAngle - fromAngle) * t);
    const paintPoint = getPaintPointAt(center, settings, angle);

    out = previousPaintPoint
      ? collectGpuAirbrushStream(state, settings, previousPaintPoint, paintPoint, dt / steps, vertices, out, exactEmission, exactEmissionMultiplier)
      : collectGpuAirbrushSpray(state, settings, paintPoint.x, paintPoint.y, dt / steps, vertices, out, exactEmission, exactEmissionMultiplier);

    previousPaintPoint = paintPoint;
  }

  state.gpuPreviousPaintPoint = previousPaintPoint;
  return out;
}

function renderGpuAirbrushVertices(ctx, state, renderer, vertices, dirty, colour, options = {}) {
  if (!ctx || !state || !renderer || !vertices || vertices.length <= 0) return false;

  const gl = renderer.gl;
  const size = state.rendererSize;
  const r = rectClamp(dirty || { x: 0, y: 0, width: size, height: size }, size, size);

  if (!r || r.width <= 0 || r.height <= 0) return false;
  if (!ensureGpuAirbrushRenderTarget(renderer)) return false;

  const data = new Float32Array(vertices);
  const stride = AIRBRUSH_GPU_FLOATS_PER_VERTEX * 4;

  gl.viewport(0, 0, size, size);
  gl.bindFramebuffer(gl.FRAMEBUFFER, renderer.framebuffer);
  gl.clearColor(0, 0, 0, 0);
  gl.clear(gl.COLOR_BUFFER_BIT);
  gl.useProgram(renderer.program);
  gl.uniform2f(renderer.resolutionLocation, size, size);
  gl.uniform3f(renderer.colorLocation, colour.r / 255, colour.g / 255, colour.b / 255);
  gl.bindBuffer(gl.ARRAY_BUFFER, renderer.buffer);
  gl.bufferData(gl.ARRAY_BUFFER, data, gl.STREAM_DRAW);

  gl.enableVertexAttribArray(renderer.centerLocation);
  gl.vertexAttribPointer(renderer.centerLocation, 2, gl.FLOAT, false, stride, 0);
  gl.enableVertexAttribArray(renderer.cornerLocation);
  gl.vertexAttribPointer(renderer.cornerLocation, 2, gl.FLOAT, false, stride, 2 * 4);
  gl.enableVertexAttribArray(renderer.radiusLocation);
  gl.vertexAttribPointer(renderer.radiusLocation, 1, gl.FLOAT, false, stride, 4 * 4);
  gl.enableVertexAttribArray(renderer.alphaLocation);
  gl.vertexAttribPointer(renderer.alphaLocation, 1, gl.FLOAT, false, stride, 5 * 4);
  gl.enableVertexAttribArray(renderer.softnessLocation);
  gl.vertexAttribPointer(renderer.softnessLocation, 1, gl.FLOAT, false, stride, 6 * 4);

  gl.enable(gl.BLEND);
  gl.blendFuncSeparate(gl.ONE, gl.ONE_MINUS_SRC_ALPHA, gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
  gl.drawArrays(gl.TRIANGLES, 0, data.length / AIRBRUSH_GPU_FLOATS_PER_VERTEX);

  const committed = commitGpuAirbrushFramebufferPatch(
    ctx,
    renderer,
    r,
    state.originX + r.x,
    state.originY + r.y,
    options,
  );

  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  return committed;
}

function drawGpuAirbrushRendererPoint(ctx, previousPoint, pointValue, options = {}, elapsedSeconds = AIRBRUSH_TIMER_INTERVAL_SECONDS) {
  if (!ctx || !pointValue) return false;

  const settings = getAirbrushModeSettings(options);

  if (!settings) return false;

  const rendererSpaceScale = getAirbrushRendererSpaceScale(options);
  const renderSettings = scaleAirbrushSettingsForRenderer(settings, rendererSpaceScale);
  const rendererPoint = toAirbrushRendererSpacePoint(pointValue, rendererSpaceScale);
  const rendererPreviousPoint = toAirbrushRendererSpacePoint(previousPoint, rendererSpaceScale);
  const state = getRenderState(options, renderSettings);

  if (!state) return false;

  const renderer = ensureGpuAirbrushRenderer(state);

  if (!renderer) return false;

  ensureRendererOrigin(state, rendererPoint);

  const colour = getAirbrushRgb(options);
  const totalDt = Math.max(0.0001, num(elapsedSeconds, AIRBRUSH_TIMER_INTERVAL_SECONDS));
  const localPoint = {
    x: rendererPoint.x - state.originX,
    y: rendererPoint.y - state.originY,
  };
  const localPreviousPoint = rendererPreviousPoint
    ? {
      x: rendererPreviousPoint.x - state.originX,
      y: rendererPreviousPoint.y - state.originY,
    }
    : null;
  const startCenter = state.gpuPreviousCenter || localPreviousPoint;
  const vertices = [];
  let remaining = totalDt;
  let elapsed = 0;
  let dirty = null;

  if (options.airbrushExactEmission === true && typeof options.airbrushEmissionDebug === 'function') {
    state.gpuEmissionDebugCounts = { small: 0, medium: 0, large: 0 };
  }

  while (remaining > 0.000001) {
    const chunk = Math.min(remaining, AIRBRUSH_RENDERER_MAX_FRAME_SECONDS);
    const fromT = totalDt > 0 ? elapsed / totalDt : 1;
    const toT = totalDt > 0 ? (elapsed + chunk) / totalDt : 1;
    const fromCenter = startCenter
      ? {
        x: startCenter.x + ((localPoint.x - startCenter.x) * fromT),
        y: startCenter.y + ((localPoint.y - startCenter.y) * fromT),
      }
      : null;
    const toCenter = startCenter
      ? {
        x: startCenter.x + ((localPoint.x - startCenter.x) * toT),
        y: startCenter.y + ((localPoint.y - startCenter.y) * toT),
      }
      : localPoint;
    const fromAngle = state.brushRotationAngle;
    const toAngle = fromAngle + getBrushRotationDelta(state, settings, chunk);

    dirty = collectGpuRotatingAirbrushDots(
      state,
      renderSettings,
      fromCenter,
      toCenter,
      fromAngle,
      toAngle,
      chunk,
      vertices,
      dirty,
      options.airbrushExactEmission === true,
      Math.max(0, Number(options.airbrushExactEmissionMultiplier) || 1),
    );

    state.brushRotationAngle = toAngle;
    elapsed += chunk;
    remaining -= chunk;
  }

  state.gpuPreviousCenter = {
    x: localPoint.x,
    y: localPoint.y,
  };

  if (state.gpuEmissionDebugCounts && typeof options.airbrushEmissionDebug === 'function') {
    const counts = state.gpuEmissionDebugCounts;
    state.gpuEmissionDebugCounts = null;
    const elapsedMs = totalDt * 1000;
    const exactEmission = options.airbrushExactEmission === true;
    const exactEmissionMultiplier = exactEmission
      ? Math.max(0, Number(options.airbrushExactEmissionMultiplier) || 1)
      : 1;
    const smallRatePerMs = Math.max(0, Number(renderSettings.smallAmount) || 0)
      * (exactEmission ? exactEmissionMultiplier : Math.max(0, Number(renderSettings.smallSpeed) || 0)) / 1000;
    const mediumRatePerMs = Math.max(0, Number(renderSettings.mediumAmount) || 0)
      * (exactEmission ? exactEmissionMultiplier : Math.max(0, Number(renderSettings.mediumSpeed) || 0)) / 1000;
    const largeRatePerMs = Math.max(0, Number(renderSettings.largeAmount) || 0)
      * (exactEmission ? exactEmissionMultiplier : Math.max(0, Number(renderSettings.largeSpeed) || 0)) / 1000;
    options.airbrushEmissionDebug({
      elapsedMs,
      emitted: {
        small: counts.small,
        medium: counts.medium,
        large: counts.large,
        total: counts.small + counts.medium + counts.large,
      },
      configuredDotsPerMs: {
        small: smallRatePerMs,
        medium: mediumRatePerMs,
        large: largeRatePerMs,
        total: smallRatePerMs + mediumRatePerMs + largeRatePerMs,
      },
      actualDotsPerMs: elapsedMs > 0
        ? (counts.small + counts.medium + counts.large) / elapsedMs
        : 0,
      remainders: { ...(state.gpuEmissionRemainders || {}) },
    });
  }

  if (vertices.length <= 0) return true;

  return renderGpuAirbrushVertices(ctx, state, renderer, vertices, dirty, colour, options);
}

function getRendererAlpha(state, index) {
  const smallInk = clamp(state.smallDensity[index], 0, 1);
  const mediumInk = clamp(state.mediumDensity[index], 0, 1);
  const largeInk = clamp(state.largeDensity[index], 0, 1);

  return 1 - ((1 - smallInk) * (1 - mediumInk) * (1 - largeInk));
}

function renderDeltaPatchToContext(ctx, state, dirty, colour, options = {}) {
  const size = state.rendererSize;
  const rendererSpaceScale = getAirbrushRendererSpaceScale(options);
  const r = rectClamp(dirty || { x: 0, y: 0, width: size, height: size }, size, size);

  if (!r) return;

  const patchCanvas = state.patchCanvas;
  const patchCtx = patchCanvas.getContext('2d');

  if (!patchCtx) return;

  patchCanvas.width = r.width;
  patchCanvas.height = r.height;

  const image = patchCtx.createImageData(r.width, r.height);
  let hasPixels = false;

  for (let y = 0; y < r.height; y += 1) {
    const row = (r.y + y) * size;

    for (let x = 0; x < r.width; x += 1) {
      const i = row + r.x + x;
      const p = (y * r.width + x) * 4;
      const currentAlpha = getRendererAlpha(state, i);
      const previousAlpha = clamp(state.previousAlpha[i], 0, 0.999999);
      const rawDelta = currentAlpha > previousAlpha
        ? (currentAlpha - previousAlpha) / Math.max(0.000001, 1 - previousAlpha)
        : 0;
      const deltaAlpha = clamp(rawDelta, 0, 1);

      state.previousAlpha[i] = currentAlpha;

      if (deltaAlpha <= 0.000001) {
        image.data[p] = 0;
        image.data[p + 1] = 0;
        image.data[p + 2] = 0;
        image.data[p + 3] = 0;
        continue;
      }

      hasPixels = true;
      image.data[p] = colour.r;
      image.data[p + 1] = colour.g;
      image.data[p + 2] = colour.b;
      image.data[p + 3] = Math.round(deltaAlpha * 255);
    }
  }

  if (!hasPixels) return;

  patchCtx.clearRect(0, 0, r.width, r.height);
  patchCtx.putImageData(image, 0, 0);

  ctx.save();
  ctx.globalCompositeOperation = options.isEraser ? 'destination-out' : 'source-over';
  ctx.globalAlpha = clamp(num(options.opacity, 1), 0, 1);
  ctx.drawImage(
    patchCanvas,
    0,
    0,
    r.width,
    r.height,
    (state.originX + r.x) / rendererSpaceScale,
    (state.originY + r.y) / rendererSpaceScale,
    r.width / rendererSpaceScale,
    r.height / rendererSpaceScale,
  );
  ctx.restore();
}

function drawRendererPoint(ctx, previousPoint, pointValue, options = {}, elapsedSeconds = AIRBRUSH_TIMER_INTERVAL_SECONDS) {
  if (!ctx || !pointValue) return;

  const settings = getAirbrushModeSettings(options);

  if (!settings) return;

  const rendererSpaceScale = getAirbrushRendererSpaceScale(options);
  const renderSettings = scaleAirbrushSettingsForRenderer(settings, rendererSpaceScale);
  const rendererPoint = toAirbrushRendererSpacePoint(pointValue, rendererSpaceScale);
  const rendererPreviousPoint = toAirbrushRendererSpacePoint(previousPoint, rendererSpaceScale);
  const state = getRenderState(options, renderSettings);

  if (!state) return;

  ensureRendererOrigin(state, rendererPoint);

  const size = state.rendererSize;
  const colour = getAirbrushRgb(options);
  const totalDt = Math.max(0.0001, num(elapsedSeconds, AIRBRUSH_TIMER_INTERVAL_SECONDS));
  const localPoint = {
    x: rendererPoint.x - state.originX,
    y: rendererPoint.y - state.originY,
  };
  const localPreviousPoint = rendererPreviousPoint
    ? {
      x: rendererPreviousPoint.x - state.originX,
      y: rendererPreviousPoint.y - state.originY,
    }
    : null;

  const startCenter = state.previousCenter || localPreviousPoint;
  let remaining = totalDt;
  let elapsed = 0;
  let dirty = null;

  while (remaining > 0.000001) {
    const chunk = Math.min(remaining, AIRBRUSH_RENDERER_MAX_FRAME_SECONDS);
    const fromT = totalDt > 0 ? elapsed / totalDt : 1;
    const toT = totalDt > 0 ? (elapsed + chunk) / totalDt : 1;
    const fromCenter = startCenter
      ? {
        x: startCenter.x + (localPoint.x - startCenter.x) * fromT,
        y: startCenter.y + (localPoint.y - startCenter.y) * fromT,
      }
      : null;
    const toCenter = startCenter
      ? {
        x: startCenter.x + (localPoint.x - startCenter.x) * toT,
        y: startCenter.y + (localPoint.y - startCenter.y) * toT,
      }
      : localPoint;
    const fromAngle = state.brushRotationAngle;
    const toAngle = fromAngle + getBrushRotationDelta(state, settings, chunk);

    dirty = merge(dirty, depositRotatingDots(
      state,
      renderSettings,
      fromCenter,
      toCenter,
      fromAngle,
      toAngle,
      chunk,
      size,
      size,
    ));

    state.brushRotationAngle = toAngle;
    elapsed += chunk;
    remaining -= chunk;
  }

  state.previousCenter = {
    x: localPoint.x,
    y: localPoint.y,
  };

  renderDeltaPatchToContext(ctx, state, dirty, colour, options);
}

function drawWithinSelection(ctx, previousPoint, pointValue, options, selectionState, elapsedSeconds) {
  if (!ctx || !pointValue) return;

  const resolvedOptions = withContextAirbrushResolution(ctx, options || {});
  ctx.save();

  if (selectionState) {
    clipContextToSelection(ctx, selectionState);
  }

  if (!drawGpuAirbrushRendererPoint(ctx, previousPoint, pointValue, resolvedOptions, elapsedSeconds)) {
    drawRendererPoint(ctx, previousPoint, pointValue, resolvedOptions, elapsedSeconds);
  }

  ctx.restore();
}


function interpolateAirbrushPoint(a, b, t) {
  const safeT = clamp(readNumber(t, 0), 0, 1);
  const ax = readNumber(a && a.x, 0);
  const ay = readNumber(a && a.y, 0);
  const bx = readNumber(b && b.x, ax);
  const by = readNumber(b && b.y, ay);
  const pressureA = Number(a && a.pressure);
  const pressureB = Number(b && b.pressure);
  const point = {
    x: ax + ((bx - ax) * safeT),
    y: ay + ((by - ay) * safeT),
  };

  if (Number.isFinite(pressureA) || Number.isFinite(pressureB)) {
    const safePressureA = Number.isFinite(pressureA) ? pressureA : 0.68;
    const safePressureB = Number.isFinite(pressureB) ? pressureB : safePressureA;
    point.pressure = safePressureA + ((safePressureB - safePressureA) * safeT);
  }

  return point;
}

function midpointAirbrushPoint(a, b) {
  return interpolateAirbrushPoint(a, b, 0.5);
}

function quadraticAirbrushPoint(start, control, end, t) {
  const startToControl = interpolateAirbrushPoint(start, control, t);
  const controlToEnd = interpolateAirbrushPoint(control, end, t);
  return interpolateAirbrushPoint(startToControl, controlToEnd, t);
}

function getAirbrushCurveSampleCount(approximateLength, options, minSteps = 2) {
  const sprayRadius = Math.max(1, getAirbrushSprayRadius(options));
  const sampleSpacing = Math.max(14, Math.min(42, sprayRadius * 0.55));
  return Math.max(minSteps, Math.min(10, Math.ceil(approximateLength / sampleSpacing)));
}

function buildQuadraticAirbrushPoints(start, control, end, options) {
  const approximateLength = Math.hypot(control.x - start.x, control.y - start.y) + Math.hypot(end.x - control.x, end.y - control.y);
  const steps = getAirbrushCurveSampleCount(approximateLength, options, 2);
  const points = [];

  for (let i = 0; i <= steps; i += 1) {
    points.push(quadraticAirbrushPoint(start, control, end, i / steps));
  }

  return points;
}

function buildLinearAirbrushPoints(start, end, options) {
  const distance = Math.hypot(end.x - start.x, end.y - start.y);
  const steps = getAirbrushCurveSampleCount(distance, options, 1);
  const points = [];

  for (let i = 0; i <= steps; i += 1) {
    points.push(interpolateAirbrushPoint(start, end, i / steps));
  }

  return points;
}

function drawAirbrushSampledPathWithinSelection(ctx, points, options, selectionState, elapsedSeconds) {
  if (!ctx || !Array.isArray(points) || points.length <= 0) return;

  if (points.length === 1) {
    drawWithinSelection(ctx, null, points[0], options, selectionState, elapsedSeconds);
    return;
  }

  const safeElapsed = Math.max(0.0001, readNumber(elapsedSeconds, AIRBRUSH_TIMER_INTERVAL_SECONDS));
  const segmentElapsed = safeElapsed / Math.max(1, points.length - 1);

  for (let i = 1; i < points.length; i += 1) {
    drawWithinSelection(ctx, points[i - 1], points[i], options, selectionState, segmentElapsed);
  }
}

export function clampAirbrushDistance(value) {
  return clamp(readNumber(value, DEFAULT_AIRBRUSH_DISTANCE), MIN_AIRBRUSH_DISTANCE, MAX_AIRBRUSH_DISTANCE);
}

export function clampAirbrushPaintFlow(value) {
  const paintFlow = readWholeNumber(value, DEFAULT_AIRBRUSH_PAINT_FLOW);

  return paintFlow === 0 || isAllowedPaintFlow(paintFlow) ? paintFlow : 0;
}

export function clampAirbrushAirPressure(value) {
  const pressure = readWholeNumber(value, DEFAULT_AIRBRUSH_AIR_PRESSURE);

  return isAllowedPressure(pressure) ? pressure : 0;
}

export function clampAirbrushNeedleLimit() {
  return DEFAULT_AIRBRUSH_NEEDLE_LIMIT;
}

export function getAirbrushSprayRadius(options = {}) {
  const settings = getAirbrushModeSettings(options) || DEFAULT_AIRBRUSH_RENDERER_SETTINGS;
  const smallRadius = Math.max(1, clamp(settings.smallDiameter, MIN_AIRBRUSH_SPRAY_DIAMETER, MAX_AIRBRUSH_SPRAY_DIAMETER) / 2);
  const mediumRadius = Math.max(1, clamp(settings.mediumDiameter, MIN_AIRBRUSH_SPRAY_DIAMETER, MAX_AIRBRUSH_SPRAY_DIAMETER) / 2);
  const largeRadius = Math.max(1, clamp(settings.largeDiameter, MIN_AIRBRUSH_SPRAY_DIAMETER, MAX_AIRBRUSH_SPRAY_DIAMETER) / 2);

  return Math.max(smallRadius, mediumRadius, largeRadius);
}

export function getAirbrushPreviewRadius(options = {}) {
  const settings = getAirbrushModeSettings(options) || DEFAULT_AIRBRUSH_RENDERER_SETTINGS;
  const smallRadius = Math.max(1, clamp(settings.smallDiameter, MIN_AIRBRUSH_SPRAY_DIAMETER, MAX_AIRBRUSH_SPRAY_DIAMETER) / 2) + Math.max(settings.smallMin, settings.smallMax) / 2;
  const mediumRadius = settings.mediumAmount > 0
    ? Math.max(1, clamp(settings.mediumDiameter, MIN_AIRBRUSH_SPRAY_DIAMETER, MAX_AIRBRUSH_SPRAY_DIAMETER) / 2) + Math.max(settings.mediumMin, settings.mediumMax) / 2
    : 0;
  const largeRadius = settings.largeAmount > 0
    ? Math.max(1, clamp(settings.largeDiameter, MIN_AIRBRUSH_SPRAY_DIAMETER, MAX_AIRBRUSH_SPRAY_DIAMETER) / 2) + Math.max(settings.largeMin, settings.largeMax) / 2
    : 0;
  const splatterExtra = settings.largeAmount > 0 && settings.splatterEnabled
    ? Math.max(settings.splatterMinLength, settings.splatterMaxLength)
    : 0;
  const paintRadius = Math.max(smallRadius, mediumRadius, largeRadius + splatterExtra);
  const rotationOffset = settings.brushRotationEnabled
    ? Math.max(
      clamp(settings.smallDiameter, MIN_AIRBRUSH_SPRAY_DIAMETER, MAX_AIRBRUSH_SPRAY_DIAMETER) / 2,
      clamp(settings.mediumDiameter, MIN_AIRBRUSH_SPRAY_DIAMETER, MAX_AIRBRUSH_SPRAY_DIAMETER) / 2,
      clamp(settings.largeDiameter, MIN_AIRBRUSH_SPRAY_DIAMETER, MAX_AIRBRUSH_SPRAY_DIAMETER) / 2,
    ) * clamp(settings.brushRotationPointRadius, 0, 100) / 100
    : 0;

  return paintRadius + rotationOffset;
}

export function getAirbrushCursorRadius(options = {}) {
  return getAirbrushPreviewRadius(options);
}

export function resetAirbrushGpuPreview(options = {}, pointValue = null) {
  const settings = getAirbrushModeSettings(options);
  if (!settings) return null;

  const rendererSpaceScale = getAirbrushRendererSpaceScale(options);
  const renderSettings = scaleAirbrushSettingsForRenderer(settings, rendererSpaceScale);
  const state = getRenderState(options, renderSettings);
  if (!state) return null;

  clearRendererDensityState(state);
  state.originX = null;
  state.originY = null;
  state.gpuPreviewInitialised = false;

  const renderer = ensureGpuAirbrushRenderer(state);
  if (!renderer) return null;
  clearGpuAirbrushPreviewSurface(renderer);

  const rendererPoint = toAirbrushRendererSpacePoint(pointValue, rendererSpaceScale);
  if (rendererPoint) ensureRendererOrigin(state, rendererPoint);
  return {
    state,
    renderer,
    canvas: renderer.canvas,
    originX: state.originX,
    originY: state.originY,
    width: state.rendererSize,
    height: state.rendererSize,
    rendererSpaceScale,
    projectionFullWidth: null,
    projectionFullHeight: null,
  };
}

export function clearAirbrushGpuPreview(options = {}) {
  const settings = getAirbrushModeSettings(options);
  if (!settings) return false;

  const rendererSpaceScale = getAirbrushRendererSpaceScale(options);
  const renderSettings = scaleAirbrushSettingsForRenderer(settings, rendererSpaceScale);
  const state = getRenderState(options, renderSettings);
  if (!state) return false;

  clearRendererDensityState(state);
  state.originX = null;
  state.originY = null;
  state.gpuPreviewInitialised = false;

  const renderer = ensureGpuAirbrushRenderer(state);
  if (!renderer) return false;
  return clearGpuAirbrushPreviewSurface(renderer);
}

export function drawAirbrushGpuPreviewPoint(previousPoint, pointValue, options = {}, elapsedSeconds = AIRBRUSH_TIMER_INTERVAL_SECONDS) {
  if (!pointValue) return null;

  const settings = getAirbrushModeSettings(options);
  if (!settings) return null;

  const rendererSpaceScale = getAirbrushRendererSpaceScale(options);
  const renderSettings = scaleAirbrushSettingsForRenderer(settings, rendererSpaceScale);
  const rendererPoint = toAirbrushRendererSpacePoint(pointValue, rendererSpaceScale);
  const rendererPreviousPoint = toAirbrushRendererSpacePoint(previousPoint, rendererSpaceScale);
  const state = getRenderState(options, renderSettings);
  if (!state) return null;

  const renderer = ensureGpuAirbrushRenderer(state);
  if (!renderer) return null;

  const previousOriginX = Number(state.originX);
  const previousOriginY = Number(state.originY);
  const hadOrigin = Number.isFinite(previousOriginX) && Number.isFinite(previousOriginY);
  const fixedOriginX = Number(options.airbrushFixedOriginX);
  const fixedOriginY = Number(options.airbrushFixedOriginY);
  const hasFixedOrigin = Number.isFinite(fixedOriginX) && Number.isFinite(fixedOriginY);
  if (hasFixedOrigin) {
    state.originX = fixedOriginX;
    state.originY = fixedOriginY;
  } else {
    ensureRendererOrigin(state, rendererPoint);
  }
  const originChanged = !hadOrigin
    || Math.abs((Number(state.originX) || 0) - previousOriginX) > 0.0001
    || Math.abs((Number(state.originY) || 0) - previousOriginY) > 0.0001;

  if (originChanged || !state.gpuPreviewInitialised) {
    clearGpuAirbrushPreviewSurface(renderer);
    state.gpuPreviewInitialised = true;
  }

  const colour = getAirbrushRgb(options);
  const totalDt = Math.max(0.0001, num(elapsedSeconds, AIRBRUSH_TIMER_INTERVAL_SECONDS));
  const localPoint = {
    x: rendererPoint.x - state.originX,
    y: rendererPoint.y - state.originY,
  };
  const localPreviousPoint = rendererPreviousPoint
    ? {
      x: rendererPreviousPoint.x - state.originX,
      y: rendererPreviousPoint.y - state.originY,
    }
    : null;
  const startCenter = state.gpuPreviousCenter || localPreviousPoint;
  const vertices = [];
  let remaining = totalDt;
  let elapsed = 0;

  while (remaining > 0.000001) {
    const chunk = Math.min(remaining, AIRBRUSH_RENDERER_MAX_FRAME_SECONDS);
    const fromT = totalDt > 0 ? elapsed / totalDt : 1;
    const toT = totalDt > 0 ? (elapsed + chunk) / totalDt : 1;
    const fromCenter = startCenter
      ? {
        x: startCenter.x + ((localPoint.x - startCenter.x) * fromT),
        y: startCenter.y + ((localPoint.y - startCenter.y) * fromT),
      }
      : null;
    const toCenter = startCenter
      ? {
        x: startCenter.x + ((localPoint.x - startCenter.x) * toT),
        y: startCenter.y + ((localPoint.y - startCenter.y) * toT),
      }
      : localPoint;
    const fromAngle = state.brushRotationAngle;
    const toAngle = fromAngle + getBrushRotationDelta(state, renderSettings, chunk);

    collectGpuRotatingAirbrushDots(
      state,
      renderSettings,
      fromCenter,
      toCenter,
      fromAngle,
      toAngle,
      chunk,
      vertices,
      null,
      options.airbrushExactEmission === true,
    );

    state.brushRotationAngle = toAngle;
    elapsed += chunk;
    remaining -= chunk;
  }

  state.gpuPreviousCenter = {
    x: localPoint.x,
    y: localPoint.y,
  };

  if (vertices.length > 0) {
    renderGpuAirbrushVerticesToPreviewSurface(state, renderer, vertices, colour);
  }

  return {
    state,
    renderer,
    canvas: renderer.canvas,
    originX: state.originX,
    originY: state.originY,
    width: state.rendererSize,
    height: state.rendererSize,
    rendererSpaceScale,
    originChanged,
  };
}

export function clearAirbrushSprayPulseState() {
  airbrushRenderState.forEach((state) => {
    clearCanvas(state.patchCanvas);
    clearRendererDensityState(state);
    state.gpuPreviewInitialised = false;
    if (state.gpuAirbrush) clearGpuAirbrushPreviewSurface(state.gpuAirbrush);
  });

  airbrushRenderState.clear();
}

export function drawAirbrushContinuousWithinSelection(
  ctx,
  previousPoint,
  pointValue,
  options,
  selectionState,
  elapsedSeconds = AIRBRUSH_TIMER_INTERVAL_SECONDS,
) {
  drawWithinSelection(ctx, previousPoint, pointValue, options, selectionState, elapsedSeconds);
}

export function drawAirbrushHoldWithinSelection(ctx, pointValue, options, selectionState) {
  drawWithinSelection(ctx, null, pointValue, options, selectionState, AIRBRUSH_TIMER_INTERVAL_SECONDS);
}

export function drawAirbrushSprayWithinSelection(ctx, previousPoint, pointValue, options, selectionState) {
  drawWithinSelection(ctx, previousPoint, pointValue, options, selectionState, AIRBRUSH_TIMER_INTERVAL_SECONDS);
}

export function drawAirbrushCurveWithinSelection(
  ctx,
  previousPreviousPoint,
  previousPoint,
  pointValue,
  options,
  selectionState,
  dabIndex = 0,
) {
  if (!ctx || !previousPreviousPoint || !previousPoint || !pointValue) return dabIndex;

  const curveStart = midpointAirbrushPoint(previousPreviousPoint, previousPoint);
  const curveEnd = midpointAirbrushPoint(previousPoint, pointValue);
  const points = buildQuadraticAirbrushPoints(curveStart, previousPoint, curveEnd, options);
  drawAirbrushSampledPathWithinSelection(ctx, points, options, selectionState, AIRBRUSH_TIMER_INTERVAL_SECONDS);

  return dabIndex + Math.max(1, points.length - 1);
}

export function drawAirbrushFinalTailWithinSelection(
  ctx,
  previousPoint,
  pointValue,
  options,
  selectionState,
  dabIndex = 0,
) {
  if (!ctx || !previousPoint || !pointValue) return dabIndex;

  const tailStart = midpointAirbrushPoint(previousPoint, pointValue);
  const points = buildLinearAirbrushPoints(tailStart, pointValue, options);
  drawAirbrushSampledPathWithinSelection(ctx, points, options, selectionState, AIRBRUSH_TIMER_INTERVAL_SECONDS);

  return dabIndex + Math.max(1, points.length - 1);
}

export function drawAirbrushContinuousCurveWithinSelection(
  ctx,
  previousPreviousPoint,
  previousPoint,
  pointValue,
  options,
  selectionState,
  elapsedSeconds = AIRBRUSH_TIMER_INTERVAL_SECONDS,
) {
  if (!ctx || !previousPreviousPoint || !previousPoint || !pointValue) return;

  const curveStart = midpointAirbrushPoint(previousPreviousPoint, previousPoint);
  const curveEnd = midpointAirbrushPoint(previousPoint, pointValue);
  const points = buildQuadraticAirbrushPoints(curveStart, previousPoint, curveEnd, options);
  drawAirbrushSampledPathWithinSelection(ctx, points, options, selectionState, elapsedSeconds);
}

export function drawAirbrushContinuousFinalTailWithinSelection(
  ctx,
  previousPoint,
  pointValue,
  options,
  selectionState,
  elapsedSeconds = AIRBRUSH_TIMER_INTERVAL_SECONDS,
) {
  if (!ctx || !previousPoint || !pointValue) return;

  const tailStart = midpointAirbrushPoint(previousPoint, pointValue);
  const points = buildLinearAirbrushPoints(tailStart, pointValue, options);
  drawAirbrushSampledPathWithinSelection(ctx, points, options, selectionState, elapsedSeconds);
}

export function drawAirbrushDabWithinSelection(
  ctx,
  pointValue,
  previousPoint,
  options,
  selectionState,
  dabIndex = 0,
) {
  drawAirbrushSprayWithinSelection(ctx, previousPoint || pointValue, pointValue, options, selectionState);

  return dabIndex + 1;
}

export function drawAirbrushSegmentWithinSelection(
  ctx,
  previousPoint,
  pointValue,
  options,
  selectionState,
  dabIndex = 0,
) {
  drawAirbrushSprayWithinSelection(ctx, previousPoint, pointValue, options, selectionState);

  return dabIndex + 1;
}

export function getAirbrushSavedModeCount() {
  return AIRBRUSH_MODE_COUNT;
}

export function getAirbrushSavedModeSettings() {
  return AIRBRUSH_SAVED_MODE_SETTINGS;
}

export function getAirbrushRendererSettings(options = {}) {
  return getAirbrushModeSettings(options);
}

export function getDefaultAirbrushRendererSettings() {
  return { ...DEFAULT_AIRBRUSH_RENDERER_SETTINGS };
}

function airbrushGpuRandom(seed) {
  const value = Math.sin((Number(seed) || 1) * 12.9898 + 78.233) * 43758.5453;
  return value - Math.floor(value);
}

function airbrushGpuRange(seed, minimum, maximum) {
  return minimum + airbrushGpuRandom(seed) * (maximum - minimum);
}

function airbrushGpuOffset(seed, radius, centreBias, settings, type, options = {}) {
  const angle = airbrushGpuRandom(seed + 1) * Math.PI * 2;
  const bias = clamp(Number(centreBias) || 0, -100, 100) / 100;
  const raw = Math.sqrt(airbrushGpuRandom(seed + 2));
  let distanceNorm = raw;

  if (bias > 0) distanceNorm = raw * (1 - bias) + (1 - Math.sqrt(airbrushGpuRandom(seed + 3))) * bias;
  if (bias < 0) distanceNorm = raw * (1 + bias) + Math.sqrt(airbrushGpuRandom(seed + 4) * (2 - airbrushGpuRandom(seed + 5))) * -bias;

  if (type === 'small') {
    const avoidChance = clamp(Number(settings.dotEdgeAvoid) || 0, 0, 100) / 100;
    const fadeStart = clamp(Number(settings.dotFadeStart) || 0, 0, 1);
    if (distanceNorm >= fadeStart && airbrushGpuRandom(seed + 6) < avoidChance) {
      distanceNorm = airbrushGpuRandom(seed + 7) * fadeStart;
    }
  }

  let naturalEdgeScale = 1;
  if (options.airbrushNaturalEdge) {
    const roughness = clamp(readNumber(options.airbrushNaturalEdgeRoughness, 0.22), 0, 0.45);
    const edgeSeed = readWholeNumber(options.strokeSeed, 1);

    // Keep the centre dense and only roughen the outside of the spray. Two
    // stable angular waves create a coherent broken silhouette, while a small
    // per-particle component prevents the wave itself looking artificial.
    const waveA = Math.sin((angle * 7) + (edgeSeed * 0.173));
    const waveB = Math.sin((angle * 13) - (edgeSeed * 0.097));
    const particleNoise = (airbrushGpuRandom(seed + 29) * 2) - 1;
    const contourNoise = (waveA * 0.5) + (waveB * 0.3) + (particleNoise * 0.2);
    const edgeBlend = clamp((distanceNorm - 0.48) / 0.52, 0, 1);
    naturalEdgeScale += contourNoise * roughness * edgeBlend;
  }

  return {
    x: Math.cos(angle) * radius * distanceNorm * naturalEdgeScale,
    y: Math.sin(angle) * radius * distanceNorm * naturalEdgeScale,
    distanceNorm,
  };
}

export function buildAirbrushGpuDabs(
  previousPoint,
  pointValue,
  options = {},
  elapsedSeconds = AIRBRUSH_TIMER_INTERVAL_SECONDS,
  runtimeState = {},
) {
  const settings = getAirbrushModeSettings(options);
  if (!settings || !pointValue) return { dabs: [], dabIndex: Number(runtimeState.dabIndex) || 0 };

  const start = previousPoint || pointValue;
  const end = pointValue;
  const elapsed = clamp(readNumber(elapsedSeconds, AIRBRUSH_TIMER_INTERVAL_SECONDS), 0.001, AIRBRUSH_RENDERER_MAX_FRAME_SECONDS);
  const pressure = options.airbrushPressurePaintFlowEnabled
    ? clamp(readNumber(end.pressure, 1), 0.02, 1)
    : 1;
  const needle = clamp(readNumber(options.airbrushNeedleLimit, DEFAULT_AIRBRUSH_NEEDLE_LIMIT), 0, 100) / 100;
  const masterOpacity = clamp(readNumber(options.opacity, 1), 0, 1);
  const strokeSeed = readWholeNumber(options.strokeSeed, 1);
  let dabIndex = Math.max(0, readWholeNumber(runtimeState.dabIndex, 0));
  const dabs = [];

  const addType = (type, amount, speed, typeOpacity, centreBias, softness, minimumSize, maximumSize, diameter) => {
    const expected = Math.max(0, amount) * Math.max(0.1, speed) * elapsed * pressure * needle;
    const count = Math.min(AIRBRUSH_GPU_MAX_DOTS_PER_PASS, Math.floor(expected + airbrushGpuRandom(strokeSeed + dabIndex * 17)));
    const radius = Math.max(1, diameter * 0.5);

    for (let index = 0; index < count; index += 1) {
      const seed = strokeSeed + (dabIndex + index) * 131 + (type === 'small' ? 11 : type === 'medium' ? 37 : 71);
      const pathT = airbrushGpuRandom(seed + 9);
      const centreX = Number(start.x) + (Number(end.x) - Number(start.x)) * pathT;
      const centreY = Number(start.y) + (Number(end.y) - Number(start.y)) * pathT;
      const offset = airbrushGpuOffset(seed, radius, centreBias, settings, type, options);
      const edgeOpacity = type === 'small'
        ? getSmallEdgeFadeOpacity(settings, offset.distanceNorm)
        : 1;
      const size = airbrushGpuRange(seed + 13, minimumSize, maximumSize);

      dabs.push({
        x: centreX + offset.x,
        y: centreY + offset.y,
        size: Math.max(0.5, size),
        softness: clamp(softness, 0, 1),
        opacity: clamp(masterOpacity * typeOpacity * edgeOpacity * pressure, 0, 1),
      });

      if (type === 'large' && settings.splatterEnabled && airbrushGpuRandom(seed + 19) < 0.15) {
        const lineCount = Math.max(0, Math.round(airbrushGpuRange(seed + 20, settings.splatterMinLines, settings.splatterMaxLines)));
        for (let line = 0; line < lineCount; line += 1) {
          const lineSeed = seed + line * 47;
          const angle = airbrushGpuRandom(lineSeed + 1) * Math.PI * 2;
          const length = airbrushGpuRange(lineSeed + 2, settings.splatterMinLength, settings.splatterMaxLength) * SPLATTER_RENDER_LENGTH_SCALE;
          const thickness = airbrushGpuRange(lineSeed + 3, settings.splatterMinThickness, settings.splatterMaxThickness) * SPLATTER_RENDER_THICKNESS_SCALE;
          const steps = getSplatterLineSteps(length, thickness);
          for (let step = 0; step <= steps; step += 1) {
            const t = step / steps;
            dabs.push({
              x: centreX + offset.x + Math.cos(angle) * length * t,
              y: centreY + offset.y + Math.sin(angle) * length * t,
              size: Math.max(0.5, thickness * getSplatterThicknessGradient(t)),
              softness: 0,
              opacity: clamp(masterOpacity * typeOpacity * pressure * (1 - t * 0.55) * 0.34, 0, 1),
            });
          }
        }
      }
    }

    dabIndex += count;
  };

  addType(
    'small',
    settings.smallAmount,
    settings.smallSpeed,
    settings.smallOpacity,
    settings.smallCentreBias,
    settings.smallSoftness,
    settings.smallMin,
    settings.smallMax,
    settings.smallDiameter,
  );
  addType(
    'medium',
    settings.mediumAmount,
    settings.mediumSpeed,
    settings.mediumOpacity,
    settings.mediumCentreBias,
    settings.mediumSoftness,
    settings.mediumMin,
    settings.mediumMax,
    settings.mediumDiameter,
  );
  addType(
    'large',
    settings.largeAmount,
    settings.largeSpeed,
    settings.largeOpacity,
    settings.largeCentreBias,
    settings.largeSoftness,
    settings.largeMin,
    settings.largeMax,
    settings.largeDiameter,
  );

  runtimeState.dabIndex = dabIndex;
  return { dabs, dabIndex };
}
